import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Project, Change, User, Track, Client, Attachment, ReplyTemplate, Comment } from '../../models';
import { Modal, Tabs,  Badge, Dropdown} from 'flowbite-react';
import { Formik, Form, ErrorMessage, type FormikProps } from 'formik';
import { Transition } from '@headlessui/react';
import { ContentState, EditorState, SelectionState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Changes, Comments, ReplyTemplates } from '../../services';
import {
    RootState, useAppSelector, useAppDispatch,
    setImageReleaseNotes, unsetImageReleaseNotes,
    loadClients, loadChange, loadProject, updateChange, loadTracks, loadUsers, loadGroups,
    addChecklist,
    loadChecklists,
    discardChange,
    restoreChange,
    createChange,
} from '../../redux';
import {
    buildLinkedChangePayload,
    canSpawnLinkedChange,
    linkedChangeCreatedCommentHtml,
    linkedChangeSourceCommentHtml,
    resolveLinkedChangePlacement,
} from '../../utils/createLinkedChange';
import {
    Ability, Icon, Button, InlineTextareaField, UserAvatarField,
    TranslatableField, TranslatableWysiwygField, TypeField, UserAvatar,
    DatepickerField, InlineField, Alert, ToggleField, ImportanceField,
    ChangeActivity, Confirm, Loader, Uploader, ChangeTrack,
    ImageUploader, ImageListType, Checklists, MoveChangeModal, MergeChangesModal,
    RelatedChangesModal,
    ReminderWidget, ClientSelectField, ClientReplyPendingBadge, CopyButton, MultiSelect, Wysiwyg, insertLink, insertImage,
    insertHtmlAtCursor, toCanonicalHtml,
    type WysiwygHandle,
    TechnicalContextPanel, FieldHelp, LabelWithHelp, Tooltip,
    RemoteImagesProvider, ShowRemoteImagesButton,
    ReplyTemplatesManageModal,
    RequesterEmails,
    ReplyRecipientsEditor,
    EmailSuggestField,
    HtmlContent,
    ChangeMergeRequestPanel,
    type IconType,
    type ReplyFromTimelinePayload,
} from '..';
import { ProgressSteps } from './ProgressSteps';
import { BudgetSteps } from './BudgetSteps';
import { Sprints } from './Sprints';
import ChangeLabelsField from './ChangeLabelsField';
import { ChangeModalBody, ChangeModalFrame, ChangeModalHeader } from './ChangeModalShell';
import ChangeStandaloneSkeleton from './ChangeStandaloneSkeleton';
import { resolveChangeModalEscapeAction } from './changeModalEscape';
import { activityPaneMaxHeightPx } from './activityPaneMaxHeight';

function scheduleIdle(callback: () => void): () => void {
    const ric = (window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
    }).requestIdleCallback;
    const cic = (window as Window & {
        cancelIdleCallback?: (id: number) => void;
    }).cancelIdleCallback;
    if (typeof ric === 'function') {
        const id = ric(callback, { timeout: 1500 });
        return () => {
            if (typeof cic === 'function')
                cic(id);
        };
    }
    const timeoutId = window.setTimeout(callback, 120);
    return () => window.clearTimeout(timeoutId);
}
import { ccEmailsOf, clientFallbackEmailOf, defaultReplyToEmail, requesterEmailsOf } from '../../utils/changeRequester';
import { buildRecipientEmailSources } from '../../utils/recipientEmailSources';
import { composeReplyMessage, peelLeadingReplyQuote } from '../../utils/replyQuote';
import { detectFalseAttachmentWording } from '../../utils/detectFalseAttachmentWording';
import {
    buildNotifiedRecipientItems,
    replyPreviewReasonI18nKey,
} from '../../utils/replyPreviewRecipients';
import {
    quotedAuthorDisplayName,
    shouldWarnQuotedAuthorNotRecipient,
    type QuotedAuthorIdentity,
} from '../../utils/quotedAuthorRecipient';
import { formatSuggestedReplyHtml } from '../../utils/formatSuggestedReplyHtml';
import {
    clearOpenChangeDocumentTitle,
    formatChangeDocumentTitle,
    setOpenChangeDocumentTitle,
} from '../../utils/changeDocumentTitle';
import { loadComposerPinned, saveComposerPinned } from '../../utils/composerPinPrefs';
import {
    compactHeaderMeta,
    loadHeaderDetailsExpanded,
    saveHeaderDetailsExpanded,
} from '../../utils/changeModalHeaderPrefs';
import { Link, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { formatMomentDate } from '../../locales';

function escapeHtmlText(text: string) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function htmlToPlainText(html: string): string {
    return (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/** Plain text of the current Draft.js selection (empty when collapsed). */
function selectedPlainText(editorState: EditorState): string {
    const selection = editorState.getSelection();
    if (selection.isCollapsed())
        return '';

    const content = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const endKey = selection.getEndKey();
    const startOffset = selection.getStartOffset();
    const endOffset = selection.getEndOffset();

    if (startKey === endKey)
        return content.getBlockForKey(startKey).getText().slice(startOffset, endOffset);

    const blocks: string[] = [];
    let inRange = false;
    content.getBlockMap().forEach((block) => {
        if (!block)
            return;
        const key = block.getKey();
        if (key === startKey) {
            inRange = true;
            blocks.push(block.getText().slice(startOffset));
            return;
        }
        if (key === endKey) {
            blocks.push(block.getText().slice(0, endOffset));
            inRange = false;
            return;
        }
        if (inRange)
            blocks.push(block.getText());
    });
    return blocks.join('\n');
}

const NON_DEV_SUBTYPE_META: Record<string, { icon: IconType; labelKey: string }> = {
    support: { icon: 'ticket', labelKey: 'changes.subtype.support' },
    sales: { icon: 'bill', labelKey: 'changes.subtype.sales' },
    oncall: { icon: 'timer', labelKey: 'changes.subtype.oncall' },
    abuse: { icon: 'warning', labelKey: 'changes.subtype.abuse' },
    ops: { icon: 'setting', labelKey: 'changes.subtype.ops' },
    other: { icon: 'dots', labelKey: 'changes.subtype.other' },
};

const CHANGE_MODAL_EXPANDED_KEY = 'yoda.changeModal.expanded';
const CHANGE_MODAL_RELATED_EXPANDED_KEY = 'yoda.changeModal.relatedExpanded';
const CHANGE_MODAL_PROGRESS_DETAIL_COLLAPSED_KEY = 'yoda.changeModal.progressDetailCollapsed';

function readChangeModalExpandedPreference(): boolean {
    try {
        return localStorage.getItem(CHANGE_MODAL_EXPANDED_KEY) === 'true';
    } catch (_e) {
        return false;
    }
}

/** Related cards block starts collapsed to keep the modal header compact. */
function readRelatedCardsExpandedPreference(): boolean {
    try {
        return localStorage.getItem(CHANGE_MODAL_RELATED_EXPANDED_KEY) === 'true';
    } catch (_e) {
        return false;
    }
}

/** Expanded Avancement tab: left detail column starts open (focus on exchanges is opt-in). */
function readProgressDetailCollapsedPreference(): boolean {
    try {
        return localStorage.getItem(CHANGE_MODAL_PROGRESS_DETAIL_COLLAPSED_KEY) === 'true';
    } catch (_e) {
        return false;
    }
}

function getChangeTypeDisplay(
    change: Change,
    t: (key: string) => string
): { icon: IconType; label: string } {
    const subtype = change.typeSubtype;
    if ((change.typeDomain === 'non_dev' || change.type === Change.TYPE_NONE) && subtype) {
        const meta = NON_DEV_SUBTYPE_META[subtype];
        if (meta)
            return { icon: meta.icon, label: t(meta.labelKey) || subtype };
    }
    const type = change.type || Change.TYPE_NONE;
    return {
        icon: Change.iconName(type),
        label: t(`projects.${type}`) || type,
    };
}

/** Drop one outer &lt;div&gt;…&lt;/div&gt; so quoted source normalizes predictably. */
function unwrapSingleRootDiv(html: string): string {
    const t = html.trim();
    const m = t.match(/^<div\b[^>]*>([\s\S]*)<\/div>$/i);
    return m ? m[1].trim() : t;
}

/**
 * Basic HTML entities decoding for plain quote text extraction.
 */
function decodeHtmlEntities(text: string): string {
    return text
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'");
}

/** Normalize HTML through Draft parser/serializer for stable WYSIWYG value updates. */
function normalizeHtmlForWysiwyg(html: string): string {
    try {
        return toCanonicalHtml(html || '');
    } catch (_e) {
        return html;
    }
}

/**
 * Convert rich HTML message into readable plain text for Draft blockquote compatibility.
 * Draft/html-to-draftjs doesn't keep nested block nodes inside blockquote reliably.
 */
function htmlToQuoteText(messageHtml: string): string {
    let text = unwrapSingleRootDiv((messageHtml || '').trim());
    text = text
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
        .replace(/<li\b[^>]*>/gi, '- ')
        .replace(/<[^>]+>/g, '');
    text = decodeHtmlEntities(text)
        .replace(/\u00a0/g, ' ')
        .replace(/\r\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    return text;
}

/**
 * Quote HTML for Draft/html-to-draftjs as a single textual blockquote.
 * Keeping it textual avoids the empty-blockquote + content-outside issue.
 */
function buildQuotedCommunicationHtml(authorLabel: string, quoteSuffix: string, messageHtml: string) {
    const trimmedAuthor = (authorLabel || '').trim();
    const attributionLine = trimmedAuthor ? `${trimmedAuthor} ${quoteSuffix}`.trim() : '';
    const messageText = htmlToQuoteText(messageHtml);
    const attributionHtml = attributionLine
        ? `<p><em>${escapeHtmlText(attributionLine)}</em></p>`
        : '';
    const messageHtmlSafe = messageText
        ? `${escapeHtmlText(messageText).replace(/\n/g, '<br/>')}`
        : '';
    const quotedBlock = `<blockquote>${messageHtmlSafe || '<br/>'}</blockquote>`;
    // Citation only — blank writing space is seeded in the body / before merge separately.
    return `${attributionHtml}${quotedBlock}`;
}

/** Two empty paragraphs so the author can type above a citation without moving the caret. */
const REPLY_LEADING_BLANK_HTML = '<p><br></p><p><br></p>';

function editorStateFromHtml(html: string): EditorState {
    try {
        const contentBlock = htmlToDraft(html || '');
        if (!contentBlock.contentBlocks?.length)
            return EditorState.createEmpty();
        return EditorState.createWithContent(
            ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap)
        );
    } catch (_e) {
        return EditorState.createEmpty();
    }
}

function moveSelectionToStart(editorState: EditorState): EditorState {
    const content = editorState.getCurrentContent();
    const firstBlock = content.getFirstBlock();
    const selection = SelectionState.createEmpty(firstBlock.getKey()).merge({
        anchorOffset: 0,
        focusOffset: 0,
    }) as SelectionState;
    return EditorState.forceSelection(editorState, selection);
}

function moveSelectionToEnd(editorState: EditorState): EditorState {
    const content = editorState.getCurrentContent();
    const lastBlock = content.getLastBlock();
    const selection = SelectionState.createEmpty(lastBlock.getKey()).merge({
        anchorOffset: lastBlock.getLength(),
        focusOffset: lastBlock.getLength(),
    }) as SelectionState;
    return EditorState.forceSelection(editorState, selection);
}

/** Build reply recipient state from a change (defaults To to demandeur, then client). */
function replyRecipientsFromChange(change?: Change | null): AnyObj {
    const empty = Change.emptyReplyRecipients();
    const current = change?.replyRecipients || {};
    const baseRecipients = {
        to: {
            ...empty.to,
            ...(current.to || {})
        },
        cc: {
            ...empty.cc,
            ...(current.cc || {})
        }
    };
    if (!baseRecipients.to.emails?.length) {
        const seed = defaultReplyToEmail(change || {});
        if (seed)
            baseRecipients.to.emails = [seed];
    }
    return baseRecipients;
}

interface ChangeModalProps {
    project: Project;
    change?: Change;
    changeId?: string;
    show: boolean;
    showProjectName?: boolean;
    onClose: () => void;
    onRemove: (values: AnyObj) => void;
    /** Pin mode: whether the current card is pinned. */
    isPinned?: boolean;
    onTogglePin?: () => void;
    /** Leave room on the right for the pinned side panel. */
    sideOffset?: boolean;
    /** Optional Tailwind padding class when sideOffset is true (e.g. 1 vs 2 pins). */
    sideOffsetClass?: string;
    /** Full-page detail (no board underneath, no Flowbite modal). */
    standalone?: boolean;
}

function ChangeModal(props: ChangeModalProps) {
    const { t, i18n } = useTranslation();
    const dispatch    = useAppDispatch();
    const navigate    = useNavigate();

    const [moveModalIsVisible, showMoveModal]   = useState(false);
    const [mergeModalIsVisible, setMergeModalVisible] = useState(false);
    const [isMerging, setIsMerging] = useState(false);
    const [mergeError, setMergeError] = useState('');
    const isMergingRef = useRef(false);
    const [relatedModalIsVisible, setRelatedModalVisible] = useState(false);
    const [isLinkingRelated, setIsLinkingRelated] = useState(false);
    const [isCreatingLinked, setIsCreatingLinked] = useState(false);
    const isCreatingLinkedRef = useRef(false);
    const [pendingUpdates, setPendingUpdates]   = useState<AnyObj>({});
    const [pendingNonDevTypeFields, setPendingNonDevTypeFields] = useState<Record<string, string> | null>(null);

    const { project, changeId, show, showProjectName, onClose, onRemove, isPinned, onTogglePin, sideOffset, sideOffsetClass, standalone } = props;

    const { user }             = useAppSelector((state: RootState) => state.auth);
    const { users, loading: usersLoading } = useAppSelector((state: RootState) => state.users);
    const { groups, loading: groupsLoading } = useAppSelector((state: RootState) => state.groups);
    const { clients, loading: clientsLoading } = useAppSelector((state: RootState) => state.clients);
    const {
        updating, change, deleting, deletingError, updateStatus, changes, triageChanges, reviewChanges,
    } = useAppSelector((state: RootState) => state.changes);
    const { board }            = useAppSelector((state: RootState) => state.boards);
    const { tracks, loading }  = useAppSelector((state: RootState) => state.tracks);

    const isLoadingtracks = loading === 'pending';
    const isClientUser = user?.role === User.ROLE_CLIENT;
    const [deleteError, setDeleteError] = useState('');
    /** Dismissed Redux delete error for this card session (avoids rehydrate on remount). */
    const dismissedDeleteErrorRef = useRef<string | null>(null);
    const deletingErrorRef = useRef(deletingError);
    deletingErrorRef.current = deletingError;

    const [selectedClient, setSelectedClient] = useState<Client | undefined>(undefined);
    const [clientChanged, setClientChanged]   = useState(false);
    const [completionNotify, setCompletionNotify] = useState<AnyObj>(Change.emptyCompletionNotify());
    const [completionEmailInput, setCompletionEmailInput] = useState('');
    const [completionNotifyError, setCompletionNotifyError] = useState('');
    const [replyDraft, setReplyDraft]         = useState('');
    /** Unsaved comment HTML — kept out of Formik so card reloads do not wipe it. */
    const [commentDraft, setCommentDraft]     = useState('');
    /** Quoted citation kept out of Draft.js while editing (reattached on send). */
    const [replyQuoteHtml, setReplyQuoteHtml] = useState('');
    const [replyQuoteExpanded, setReplyQuoteExpanded] = useState(false);
    const [replyError, setReplyError]         = useState('');
    const [replySuccess, setReplySuccess]     = useState('');
    const [isSuggestingReply, setIsSuggestingReply] = useState(false);
    const [isSendingReply, setIsSendingReply]       = useState(false);
    const [showAttachmentWordingConfirm, setShowAttachmentWordingConfirm] = useState(false);
    const [replyTemplates, setReplyTemplates] = useState<ReplyTemplate[]>([]);
    const [replyTemplatesLoading, setReplyTemplatesLoading] = useState(false);
    const [replyTemplatesManageOpen, setReplyTemplatesManageOpen] = useState(false);
    const [replyEditorState, setReplyEditorState] = useState<EditorState | undefined>(undefined);
    const replyEditorStateRef = useRef<EditorState | undefined>(undefined);
    const replyWysiwygRef = useRef<WysiwygHandle>(null);
    const [replyRecipients, setReplyRecipients] = useState<AnyObj>(Change.emptyReplyRecipients());
    const [replyRecipientsDirty, setReplyRecipientsDirty] = useState(false);
    const [toEmailInput, setToEmailInput] = useState('');
    const [ccEmailInput, setCcEmailInput] = useState('');
    const [isSavingReplyRecipients, setIsSavingReplyRecipients] = useState(false);
    const [replyRecipientsError, setReplyRecipientsError] = useState('');
    const [replyRecipientsSuccess, setReplyRecipientsSuccess] = useState('');
    const [replyRecipientsEditorVisible, setReplyRecipientsEditorVisible] = useState(false);
    const [replyRecipientsEditorAnchor, setReplyRecipientsEditorAnchor] = useState<'header' | 'composer'>('composer');
    const [communicationTab, setCommunicationTab] = useState<'reply' | 'comment'>('reply');
    const [communicationComposerOpen, setCommunicationComposerOpen] = useState(false);
    const [composerPinned, setComposerPinned] = useState(loadComposerPinned);
    const communicationToolbarRef = useRef<HTMLDivElement>(null);
    const activityScrollRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(readChangeModalExpandedPreference);
    const [headerDetailsExpanded, setHeaderDetailsExpanded] = useState(loadHeaderDetailsExpanded);
    const [relatedExpanded, setRelatedExpanded] = useState(readRelatedCardsExpandedPreference);
    const [progressDetailCollapsed, setProgressDetailCollapsed] = useState(readProgressDetailCollapsedPreference);
    const [editingMeta, setEditingMeta] = useState<null | 'type' | 'client' | 'importance'>(null);
    const [isTogglingChangeNotifications, setIsTogglingChangeNotifications] = useState(false);
    const [isSavingSubscribers, setIsSavingSubscribers] = useState(false);
    const [replyRecipientsPreview, setReplyRecipientsPreview] = useState<AnyObj | null>(null);
    const [replyRecipientsPreviewError, setReplyRecipientsPreviewError] = useState('');
    const [isLoadingReplyRecipientsPreview, setIsLoadingReplyRecipientsPreview] = useState(false);
    const [quotedReplyAuthor, setQuotedReplyAuthor] = useState<QuotedAuthorIdentity | null>(null);
    const [changeNotFound, setChangeNotFound] = useState(false);
    const formikRef = React.useRef<FormikProps<AnyObj>>(null);

    const mergedTargetProject: AnyObj = change?.mergedInto?.project
        ? { _id: change.mergedInto.project, name: change.project?.name }
        : change?.project;
    const mergedTargetPath = change?.mergedInto?.change
        ? Project.path(mergedTargetProject, 'change', change.mergedInto.change)
        : null;

    const openMergeModal = () => {
        setMergeError('');
        setMergeModalVisible(true);
    };

    const closeMergeModal = () => {
        if (isMerging)
            return;
        setMergeError('');
        setMergeModalVisible(false);
    };

    const applyMerge = async (target: Change) => {
        if (!change?._id || !target?._id || isMergingRef.current)
            return;
        isMergingRef.current = true;
        setIsMerging(true);
        setMergeError('');
        try {
            await Changes.merge(target._id, [change._id]);
            setMergeModalVisible(false);
            onClose();
            window.location.assign(Project.path(target.project, 'change', target._id));
        } catch (err) {
            const message = err instanceof Error && err.message
                ? err.message
                : (t('priorities.merge_failed') as string);
            setMergeError(message);
        } finally {
            isMergingRef.current = false;
            setIsMerging(false);
        }
    };

    const applyRelatedLink = async (related: Change) => {
        if (!change?._id || !related?._id)
            return;
        setIsLinkingRelated(true);
        try {
            await Changes.addRelated(change._id, related._id);
            setRelatedModalVisible(false);
            await dispatch(loadChange(change._id));
        } finally {
            setIsLinkingRelated(false);
        }
    };

    const createLinkedChange = async (seedComment?: Comment) => {
        // Menu path: finished/deployed only. Message path: any open card with a client message.
        const fromClientMessage = !!seedComment;
        if (!change?._id || isCreatingLinkedRef.current)
            return;
        if (change.isMerged?.() || change.progress?.status === Change.STATUS_DISCARDED)
            return;
        if (!fromClientMessage && !canSpawnLinkedChange(change))
            return;

        isCreatingLinkedRef.current = true;
        setIsCreatingLinked(true);
        try {
            const titlePrefix = String(t('changes.create_linked_change_title_prefix') || 'Suite : ');
            // Seed client text as a replyable timeline message, not as description.
            const payload = buildLinkedChangePayload(change, { titlePrefix });
            const placement = resolveLinkedChangePlacement(change, board, project);
            const params = (placement.boardId && placement.columnId)
                ? { boardId: placement.boardId, columnId: placement.columnId }
                : undefined;

            const created = await new Promise<Change>((resolve, reject) => {
                dispatch(createChange(payload, params, (err, result) => {
                    if (err || !result)
                        reject(err || new Error(String(t('changes.create_linked_change_failed'))));
                    else
                        resolve(result as Change);
                }));
            });

            await Changes.addRelated(change._id, created._id);

            const createdLabel = String(t('changes.create_linked_change_created_comment') || 'Suite créée :');
            const sourceLabel = String(t('changes.create_linked_change_source_comment') || 'Suite de');
            const sourceProjectId = _.isObject(change.project) ? change.project._id : change.project;
            const createdProjectId = _.isObject(created.project)
                ? created.project._id
                : (created.project || sourceProjectId);

            // Pass `{ _id }` refs — Comment/Project constructors treat a bare string
            // as a props object and drop `_id`, which makes POST /comments fail validation.
            // Prefer Comments.create over redux createComment so we don't append these
            // comments onto the *currently open* card in the store.
            const postComment = (body: AnyObj) => Comments.create(body).then((data: AnyObj) => {
                if (!data?.comment)
                    throw new Error(String(t('changes.create_linked_change_failed')));
                return data.comment;
            });

            await postComment({
                change: { _id: change._id },
                project: { _id: sourceProjectId },
                message: linkedChangeCreatedCommentHtml(created, createdLabel),
            });
            await postComment({
                change: { _id: created._id },
                project: { _id: createdProjectId },
                message: linkedChangeSourceCommentHtml(change, sourceLabel),
            });
            if (seedComment?.message) {
                await postComment({
                    change: { _id: created._id },
                    project: { _id: createdProjectId },
                    message: seedComment.message,
                    originalMessage: seedComment.originalMessage || null,
                    // Keep intake styling / Afficher l'original / Répondre behaviour.
                    source: seedComment.source || 'email_intake',
                    kind: 'comment',
                });
            }

            onClose();
            navigate(Project.path(created.project || change.project, 'change', created._id));
        } catch (err) {
            console.error(err);
            window.alert(String(t('changes.create_linked_change_failed')));
        } finally {
            isCreatingLinkedRef.current = false;
            setIsCreatingLinked(false);
        }
    };

    const unlinkRelated = async (relatedId: string) => {
        if (!change?._id || !relatedId)
            return;
        setIsLinkingRelated(true);
        try {
            await Changes.removeRelated(change._id, relatedId);
            await dispatch(loadChange(change._id));
        } finally {
            setIsLinkingRelated(false);
        }
    };

    const mergeIntoCandidate = async () => {
        const candidateId = change?.technicalContext?.mergeReview?.candidateId;
        if (!candidateId || !change?._id || isMergingRef.current)
            return;
        isMergingRef.current = true;
        setIsMerging(true);
        setMergeError('');
        try {
            await Changes.merge(candidateId, [change._id]);
            onClose();
            window.location.reload();
        } catch (err) {
            const message = err instanceof Error && err.message
                ? err.message
                : (t('priorities.merge_failed') as string);
            setMergeError(message);
        } finally {
            isMergingRef.current = false;
            setIsMerging(false);
        }
    };

    const handleModalClose = useCallback(() => {
        if (mergeModalIsVisible || relatedModalIsVisible || replyTemplatesManageOpen)
            return;
        onClose();
    }, [mergeModalIsVisible, relatedModalIsVisible, replyTemplatesManageOpen, onClose]);

    const toggleHeaderDetails = useCallback(() => {
        setHeaderDetailsExpanded((prev) => {
            const next = !prev;
            saveHeaderDetailsExpanded(next);
            return next;
        });
    }, []);

    const toggleExpanded = useCallback(() => {
        setIsExpanded((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(CHANGE_MODAL_EXPANDED_KEY, next ? 'true' : 'false');
            } catch (_e) {
                // Ignore storage failures (private mode, quotas, etc.).
            }
            return next;
        });
    }, []);

    const toggleRelatedExpanded = useCallback(() => {
        setRelatedExpanded((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(CHANGE_MODAL_RELATED_EXPANDED_KEY, next ? 'true' : 'false');
            } catch (_e) {
                // Ignore storage failures (private mode, quotas, etc.).
            }
            return next;
        });
    }, []);

    const toggleProgressDetailCollapsed = useCallback(() => {
        setProgressDetailCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(CHANGE_MODAL_PROGRESS_DETAIL_COLLAPSED_KEY, next ? 'true' : 'false');
            } catch (_e) {
                // Ignore storage failures (private mode, quotas, etc.).
            }
            return next;
        });
    }, []);

    const toggleComposerPinned = useCallback(() => {
        setComposerPinned((prev) => {
            const next = !prev;
            saveComposerPinned(next);
            return next;
        });
    }, []);

    useEffect(() => {
        setIsExpanded(readChangeModalExpandedPreference());
        setHeaderDetailsExpanded(loadHeaderDetailsExpanded());
        setRelatedExpanded(readRelatedCardsExpandedPreference());
        setProgressDetailCollapsed(readProgressDetailCollapsedPreference());
        setComposerPinned(loadComposerPinned());
    }, [show, changeId]);

    // Cap the activity pane to the remaining space in its layout parent (grid
    // row) on lg+ only. Below lg the page uses a single App/outlet scroll with
    // natural stacking — no nested maxHeight. Viewport-only math used to clamp
    // to 160px when standalone pages grew tall and the pane sat below the fold.
    useLayoutEffect(() => {
        const layoutExpanded = !!standalone || isExpanded;
        if (!show || !layoutExpanded)
            return undefined;

        const el = activityScrollRef.current;
        if (!el)
            return undefined;

        const stackedMq = window.matchMedia('(max-width: 1023px)');
        const update = () => {
            if (stackedMq.matches) {
                el.style.maxHeight = '';
                return;
            }
            const top = el.getBoundingClientRect().top;
            const parent = el.parentElement;
            const parentBottom = parent?.getBoundingClientRect().bottom;
            // Prefer the grid parent's bottom; fall back to the viewport.
            const boundBottom = parentBottom ?? window.innerHeight;
            el.style.maxHeight = `${activityPaneMaxHeightPx({ top, boundBottom })}px`;
        };

        update();
        stackedMq.addEventListener('change', update);
        window.addEventListener('resize', update);
        const observer = new ResizeObserver(update);
        observer.observe(document.documentElement);
        if (el.parentElement)
            observer.observe(el.parentElement);

        return () => {
            stackedMq.removeEventListener('change', update);
            window.removeEventListener('resize', update);
            observer.disconnect();
            el.style.maxHeight = '';
        };
    }, [
        show,
        standalone,
        isExpanded,
        progressDetailCollapsed,
        communicationComposerOpen,
        composerPinned,
        changeId,
        change?.updatedAt,
    ]);

    useEffect(() => {
        if (!show)
            clearOpenChangeDocumentTitle();
        return () => clearOpenChangeDocumentTitle();
    }, [show]);

    const ensureUsersLoaded = useCallback(() => {
        if (usersLoading === 'pending' || (users && users.length > 0))
            return;
        dispatch(loadUsers());
    }, [dispatch, users, usersLoading]);

    const ensureClientsLoaded = useCallback(() => {
        if (clientsLoading === 'pending' || (clients && clients.length > 0))
            return;
        dispatch(loadClients());
    }, [dispatch, clients, clientsLoading]);

    const ensureGroupsLoaded = useCallback(() => {
        if (groupsLoading === 'pending' || (groups && groups.length > 0))
            return;
        dispatch(loadGroups());
    }, [dispatch, groups, groupsLoading]);

    useEffect(() => {
        if (!changeId) {
            setChangeNotFound(false);
            return;
        }
        let cancelled = false;
        setChangeNotFound(false);
        dispatch(loadChange(changeId, (err: Error | null) => {
            if (!cancelled && err)
                setChangeNotFound(true);
        }));
        return () => {
            cancelled = true;
        };
    }, [dispatch, changeId]);

    // Secondary directories after first paint — not needed to render the card shell.
    useEffect(() => {
        if (!change?._id || !show)
            return;
        return scheduleIdle(() => {
            ensureUsersLoaded();
            ensureGroupsLoaded();
        });
    }, [change?._id, show, ensureUsersLoaded, ensureGroupsLoaded]);

    useEffect(() => {
        if (editingMeta === 'client')
            ensureClientsLoaded();
    }, [editingMeta, ensureClientsLoaded]);

    useEffect(() => {
        if (replyRecipientsEditorVisible || communicationComposerOpen)
            ensureGroupsLoaded();
    }, [replyRecipientsEditorVisible, communicationComposerOpen, ensureGroupsLoaded]);

    useEffect(() => {
        // Prefer the fully loaded Redux change: props.change is often a board summary without client.
        const clientObject = change?.client ?? props.change?.client;
        if (!clientChanged) {
            setSelectedClient(clientObject as unknown as Client);
        }
        setClientChanged(false);
    }, [change?.updatedAt, change?.client, props.change?.updatedAt, props.change?.client]);

    useEffect(() => {
        const empty = Change.emptyCompletionNotify();
        const current = change?.completionNotify || {};
        setCompletionNotify({
            emails: [...(current.emails || empty.emails)],
            signalUsers: [...(current.signalUsers || empty.signalUsers)],
            signalGroups: [...(current.signalGroups || empty.signalGroups)],
        });
        setCompletionEmailInput('');
        setCompletionNotifyError('');
    }, [change?._id, change?.updatedAt, show]);

    const members = React.useMemo(() => {
        if (!project || !users)
            return [];

        return users.filter((user: User) => project.hasMember(user));
    }, [project, users]);

    // Members + current non-member subscribers (kept after cross-project moves).
    const subscriberCandidates = React.useMemo(() => {
        if (!users)
            return members;

        const subscriberIds = new Set((change?.subscribers || []).map(String));
        const byId = new Map<string, User>();
        members.forEach((member: User) => byId.set(String(member._id), member));
        users.forEach((candidate: User) => {
            if (subscriberIds.has(String(candidate._id)))
                byId.set(String(candidate._id), candidate);
        });
        return Array.from(byId.values());
    }, [change?.subscribers, members, users]);

    const assignableMembers = React.useMemo(() => {
        if (!project || !users || !user.can('change:assign_self'))
            return [];

        const projectMembers = users.filter((user: User) => project.hasMember(user));
        if (user.can('change:assign'))
            return projectMembers;

        return projectMembers.filter((u: User) => u._id === user._id);
    }, [project, users]);

    const trackedDuration = React.useMemo(() => {
        if (!tracks)
            return null;

        const duration = tracks
            .filter((track: Track) => track.change?._id === change?._id)
            .reduce((acc: number, track: Track) => acc + track.seconds(), 0);

        return duration > 0 ? Track.format(duration) : null;
    }, [change?.updatedAt, tracks]);

    const [columnTitle, setColumnTitle] = useState('');

    const isCreation = !(change?._id?.length > 0);

    useEffect(() => {
        if (!change || !project)
            return;

        board?.columns?.forEach((column: AnyObj) => {
            if (column.changes.find((c: Change) => c._id === change._id))
                setColumnTitle(column.title);
        });
    }, [board?.columns, change?.updatedAt, project]);

    useEffect(() => {
        setDeleteError('');
        // Snapshot Redux error at card/modal switch so remount cannot rehydrate it.
        dismissedDeleteErrorRef.current = deletingErrorRef.current;
    }, [changeId, show]);

    useEffect(() => {
        if (deleting === 'pending')
            setDeleteError('');
    }, [deleting]);

    useEffect(() => {
        if (!deletingError) {
            dismissedDeleteErrorRef.current = null;
            return;
        }
        if (deletingError === dismissedDeleteErrorRef.current)
            return;

        dismissedDeleteErrorRef.current = deletingError;
        if (/time tracking/i.test(deletingError))
            setDeleteError(t('changes.delete_failed_has_tracks') || deletingError);
        else
            setDeleteError(t('changes.delete_failed') || deletingError);
    }, [deletingError, t]);

    // Reset composer/draft only when switching card or (re)opening the modal —
    // not on every card mutation (start, recipients, status, …).
    useEffect(() => {
        setReplyDraft('');
        setCommentDraft('');
        setReplyQuoteHtml('');
        setReplyQuoteExpanded(false);
        setQuotedReplyAuthor(null);
        replyEditorStateRef.current = undefined;
        setReplyEditorState(undefined);
        setReplyError('');
        setReplySuccess('');
        setReplyRecipientsError('');
        setReplyRecipientsSuccess('');
        setReplyRecipientsDirty(false);
        setReplyRecipientsEditorVisible(false);
        setReplyRecipientsEditorAnchor('composer');
        setCommunicationTab('reply');
        setCommunicationComposerOpen(false);
        setEditingMeta(null);
        setToEmailInput('');
        setCcEmailInput('');
        setReplyRecipientsPreview(null);
        setReplyRecipientsPreviewError('');
        setReplyRecipients(replyRecipientsFromChange(change));
    }, [change?._id, show]);

    // Keep recipients aligned with the server without closing the open composer.
    useEffect(() => {
        if (!show || !change?._id)
            return;
        if (replyRecipientsDirty)
            return;
        setReplyRecipients(replyRecipientsFromChange(change));
        // Intentionally omit replyRecipientsDirty from deps: only re-sync on server updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [change?._id, change?.updatedAt, show]);

    // Clients can only reply (no internal comments)
    useEffect(() => {
        if (isClientUser && communicationTab !== 'reply')
            setCommunicationTab('reply');
    }, [isClientUser, communicationTab]);

    useEffect(() => {
        if (!change?._id || !show)
            return;
        return scheduleIdle(() => {
            dispatch(loadTracks({
                from: change.createdAt,
                change: change._id
            }));
        });
    }, [dispatch, change?._id, change?.createdAt, show]);

    const onChangeField = async (field: string, value: any ) => {
        _.set(pendingUpdates, field, value);
        setPendingUpdates(pendingUpdates);
    }

    const onSaveField = async (field: string, value: any ) => {
        const params = { "_id":  change?._id, ...pendingUpdates };
        // UserAvatarField writes `assignee._id`; empty string means unassign.
        if (field === 'assignee._id' && (value === '' || value == null))
            _.set(params, 'assignee', null);
        else
            _.set(params, field, value);

        await dispatch(updateChange(params, /*patch*/true, /*optimistic*/true, () => {
            setPendingUpdates({});
        }));
    }

    const DEV_PIPELINE_STATUSES = [
        Change.STATUS_TEAM_REVIEW,
        Change.STATUS_CLIENT_REVIEW,
        Change.STATUS_PENDING_DEPLOYMENT,
        Change.STATUS_DEPLOYED,
    ];

    const onSaveMultipleFields = async (fields: Record<string, any>) => {
        const params = { "_id": change?._id, ...pendingUpdates, ...fields };
        await dispatch(updateChange(params, /*patch*/true, /*optimistic*/true, () => {
            setPendingUpdates({});
        }));
    }

    const saveTypeFields = async (fields: Record<string, string>) => {
        await onSaveMultipleFields(fields);
        setEditingMeta(null);
        setPendingNonDevTypeFields(null);
    };

    const onSaveTypeFields = (fields: Record<string, string>) => {
        const switchingToNonDev = fields.typeDomain === 'non_dev' || fields.type === Change.TYPE_NONE;
        const willRewind = switchingToNonDev
            && change?.isDev()
            && DEV_PIPELINE_STATUSES.includes(change?.progress?.status);

        if (willRewind) {
            setPendingNonDevTypeFields(fields);
            return;
        }

        return saveTypeFields(fields);
    };

    const handleKeyPress = (e: AnyObj) => {
        if (e.key === 'Escape' || e.code === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            const action = resolveChangeModalEscapeAction({
                editingMeta,
                communicationComposerOpen,
                mergeModalIsVisible,
                relatedModalIsVisible,
                replyTemplatesManageOpen,
                pendingNonDevTypeFields,
            });
            if (action === 'cancelMeta') {
                setEditingMeta(null);
                return;
            }
            if (action === 'closeComposer') {
                setCommunicationComposerOpen(false);
                return;
            }
            if (action === 'closeModal')
                handleModalClose();
        }
    }

    const onImageRelease = (imageList: ImageListType) => {
        if (imageList.length > 0) {
            const file: File | undefined = imageList[0].file;
            if (file) {
                return dispatch(setImageReleaseNotes(change?._id, file));
            }
        }
    };

    const removeImageRelease = () => {
        dispatch(unsetImageReleaseNotes(change?._id));
    }

    // Adds a new to-do list to the checklist.
    const addToDoList = () => {
        const checklist = { name : t('changes.checklist.new_list'), items: [], project, change, user };

        dispatch(addChecklist(checklist, {}, (err: Error | null, result?: AnyObj | AnyObj[] | null) => {
            dispatch(loadChecklists({change: change?._id}));
            document.getElementById('checklists-component')?.scrollIntoView({ behavior: 'smooth' });
        }));
    }

    const userCanDeleteThisCard = user.can('change:delete') ? true : (
        user.can('change:delete:own') && (change?.author?._id === user?._id)
    );

    const discardInFlightRef = useRef(false);
    const handleDiscard = useCallback((card?: Change | AnyObj | null) => {
        const target = card || change;
        const id = target?._id;
        if (!id || discardInFlightRef.current || deleting === 'pending')
            return;
        discardInFlightRef.current = true;
        dispatch(discardChange(id, (err: Error | null) => {
            discardInFlightRef.current = false;
            if (err)
                return;
            // Parent onRemove is UI cleanup only (reload lists); soft-delete already done.
            onRemove(target);
            // Modal overlay: close. Standalone full-page view: stay on the discarded
            // card (banner + restore) instead of dumping the user on the project page.
            if (!standalone)
                onClose();
        }));
    }, [change, deleting, dispatch, onClose, onRemove, standalone]);

    const handleRestore = useCallback(() => {
        if (!change?._id)
            return;
        dispatch(restoreChange(change._id));
    }, [change?._id, dispatch]);
    const createdByAndAt = () => {
        if (change?.author && change.createdAt)
            return change.author.fullname() + " - " + formatMomentDate(change.createdAt, 'll');

        return "";
    };

    const uniqueValues = (values: any[]) => {
        const seen = new Set<string>();
        return (values || [])
            .map((value: any) => (typeof value === 'string' ? value.trim() : ''))
            .filter((value: string) => !!value)
            .filter((value: string) => {
                const key = value.toLowerCase();
                if (seen.has(key))
                    return false;
                seen.add(key);
                return true;
            });
    };

    const uniqueEmails = (values: any[]) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return uniqueValues(values).filter(email => emailRegex.test(email));
    };
    const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const updateCompletionNotify = (key: 'emails' | 'signalUsers' | 'signalGroups', values: string[]) => {
        const next = {
            emails: uniqueEmails(completionNotify.emails || []),
            signalUsers: [...(completionNotify.signalUsers || [])],
            signalGroups: [...(completionNotify.signalGroups || [])],
            [key]: key === 'emails' ? uniqueEmails(values) : values,
        };
        setCompletionNotify(next);
        setCompletionNotifyError('');
        onSaveField('completionNotify', next);
    };

    const addCompletionEmail = (raw?: string) => {
        const email = (raw ?? completionEmailInput).trim();
        if (!email)
            return;
        if (!isValidEmail(email)) {
            setCompletionNotifyError(t('changes.reply_invalid_email') || '');
            return;
        }
        updateCompletionNotify('emails', [
            ...(completionNotify.emails || []),
            email
        ]);
        setCompletionEmailInput('');
    };

    const removeCompletionEmail = (email: string) => {
        updateCompletionNotify(
            'emails',
            (completionNotify.emails || []).filter((value: string) => value !== email)
        );
    };

    const normalizeRecipients = (value: AnyObj) => {
        const empty = Change.emptyReplyRecipients();
        const source = value || {};
        return {
            to: {
                emails: uniqueEmails(source.to?.emails || empty.to.emails),
                // Signal recipients are NOT email recipients: keep them separate from To/Cc semantics.
                signalUsers: uniqueValues(source.to?.signalUsers || empty.to.signalUsers),
                signalGroups: uniqueValues(source.to?.signalGroups || empty.to.signalGroups),
            },
            cc: {
                emails: uniqueEmails(source.cc?.emails || empty.cc.emails),
                // Do not treat Signal users/groups as "Cc".
                signalUsers: [],
                signalGroups: [],
            }
        };
    };

    const areSameRecipients = (a: AnyObj, b: AnyObj) => JSON.stringify(normalizeRecipients(a)) === JSON.stringify(normalizeRecipients(b));

    const updateRecipientSection = (section: 'to' | 'cc', key: 'emails' | 'signalUsers' | 'signalGroups', values: string[]) => {
        const current = normalizeRecipients(replyRecipients);
        const next = normalizeRecipients({
            ...replyRecipients,
            [section]: {
                ...(replyRecipients?.[section] || {}),
                [key]: values
            }
        });
        if (areSameRecipients(current, next))
            return;
        setReplyRecipients(next);
        setReplyRecipientsDirty(true);
        setReplyRecipientsError('');
        setReplyRecipientsSuccess('');
    };

    const addEmailRecipient = (section: 'to' | 'cc', raw?: string) => {
        const value = raw ?? (section === 'to' ? toEmailInput : ccEmailInput);
        const email = value.trim();
        if (!email)
            return;
        if (!isValidEmail(email)) {
            setReplyRecipientsError(t('changes.reply_invalid_email') || '');
            return;
        }

        updateRecipientSection(section, 'emails', [
            ...((replyRecipients?.[section]?.emails || []) as string[]),
            email
        ]);

        if (section === 'to')
            setToEmailInput('');
        else
            setCcEmailInput('');
    };

    const recipientEmailSources = React.useMemo(() => buildRecipientEmailSources({
        users,
        clients,
        changes: [change, ...(changes || []), ...(triageChanges || []), ...(reviewChanges || [])],
        extraEmails: [
            ...(replyRecipients?.to?.emails || []),
            ...(replyRecipients?.cc?.emails || []),
            ...(completionNotify?.emails || []),
        ],
    }), [users, clients, change, changes, triageChanges, reviewChanges, replyRecipients, completionNotify]);

    const replyRecipientMemberOptions = React.useMemo(
        () => members.map((member: User) => ({ value: member._id, label: member.fullname() })),
        [members]
    );

    const replyRecipientGroupOptions = React.useMemo(
        () => (groups || []).map((group: AnyObj) => ({ value: group._id, label: group.name })),
        [groups]
    );

    const toggleReplyRecipientsEditor = (anchor: 'header' | 'composer') => {
        if (replyRecipientsEditorVisible && replyRecipientsEditorAnchor === anchor) {
            setReplyRecipientsEditorVisible(false);
            return;
        }
        setReplyRecipientsEditorAnchor(anchor);
        setReplyRecipientsEditorVisible(true);
    };

    const notifiedRecipientItems = React.useMemo(
        () => buildNotifiedRecipientItems(replyRecipientsPreview),
        [replyRecipientsPreview]
    );

    const composerClientFallbackEmail = clientFallbackEmailOf({
        ...(change || {}),
        client: (selectedClient || change?.client) as { email?: string } | null,
    });

    const showQuotedAuthorNotRecipientWarning = !isClientUser
        && communicationComposerOpen
        && communicationTab === 'reply'
        && shouldWarnQuotedAuthorNotRecipient({
            author: quotedReplyAuthor,
            currentUserId: user?._id,
            replyRecipients,
            clientFallbackEmail: composerClientFallbackEmail,
            notifiedItems: notifiedRecipientItems,
            previewReady: !isLoadingReplyRecipientsPreview && replyRecipientsPreview != null,
        });

    const addQuotedAuthorToRecipients = () => {
        if (!quotedReplyAuthor)
            return;
        const email = String(quotedReplyAuthor.email || '').trim();
        if (email) {
            addEmailRecipient('to', email);
            return;
        }
        const userId = String(quotedReplyAuthor.userId || '').trim();
        if (!userId)
            return;
        const current = (replyRecipients?.to?.signalUsers || []) as string[];
        if (current.includes(userId))
            return;
        updateRecipientSection('to', 'signalUsers', [...current, userId]);
    };

    const removeEmailRecipient = (section: 'to' | 'cc', email: string) => {
        updateRecipientSection(
            section,
            'emails',
            ((replyRecipients?.[section]?.emails || []) as string[]).filter((value: string) => value !== email)
        );
    };

    const syncReplyEditor = (newState: EditorState) => {
        replyEditorStateRef.current = newState;
        setReplyEditorState(newState);
        if (replyWysiwygRef.current) {
            replyWysiwygRef.current.applyEditorState(newState);
            return;
        }
        setReplyDraft(normalizeHtmlForWysiwyg(draftToHtml(convertToRaw(newState.getCurrentContent()))));
    };

    const insertHtmlIntoReply = (html: string) => {
        const trimmed = (html || '').trim();
        if (!trimmed)
            return;

        let baseState = replyEditorStateRef.current;
        if (!baseState) {
            baseState = replyDraft ? editorStateFromHtml(replyDraft) : EditorState.createEmpty();
            // No live cursor: append at the end of existing content.
            if (replyDraft)
                baseState = moveSelectionToEnd(baseState);
        }

        syncReplyEditor(insertHtmlAtCursor(baseState, trimmed));
    };

    const insertReplyAttachment = (attachment: Attachment) => {
        const baseState = replyEditorStateRef.current
            || replyEditorState
            || (replyDraft ? editorStateFromHtml(replyDraft) : EditorState.createEmpty());

        const newState = attachment.isPicture() ?
            insertImage(baseState, attachment.downloadUrl(), attachment.name) :
            insertLink(baseState, attachment.name, attachment.downloadUrl());
        if (!newState)
            return;

        syncReplyEditor(newState);
    };

    const saveReplyRecipients = async () => {
        if (!change)
            return;
        try {
            setReplyRecipientsError('');
            setReplyRecipientsSuccess('');
            setIsSavingReplyRecipients(true);
            const normalized = normalizeRecipients(replyRecipients);
            await Changes.updateReplyRecipients(change._id, normalized);
            setReplyRecipients(normalized);
            setReplyRecipientsDirty(false);
            setReplyRecipientsEditorVisible(false);
            setReplyRecipientsSuccess(t('changes.reply_recipients_saved') || '');
            await dispatch(loadChange(change._id));
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : '';
            if (message.includes('Cannot PUT')) {
                try {
                    await dispatch(updateChange({
                        _id: change._id,
                        replyRecipients: normalizeRecipients(replyRecipients)
                    }, /*patch*/true, /*optimistic*/false));
                    setReplyRecipientsDirty(false);
                    setReplyRecipientsEditorVisible(false);
                    setReplyRecipientsSuccess(t('changes.reply_recipients_saved') || '');
                    await dispatch(loadChange(change._id));
                    return;
                } catch (fallbackError: unknown) {
                    setReplyRecipientsError(
                        fallbackError instanceof Error ?
                            fallbackError.message :
                            (t('changes.reply_recipients_save_failed') || '')
                    );
                }
            } else {
                setReplyRecipientsError(message || (t('changes.reply_recipients_save_failed') || ''));
            }
        } finally {
            setIsSavingReplyRecipients(false);
        }
    };

    const replyRecipientsEditorNode = !isClientUser && replyRecipientsEditorVisible ? (
        <ReplyRecipientsEditor
            replyRecipients={replyRecipients}
            toEmailInput={toEmailInput}
            ccEmailInput={ccEmailInput}
            onToEmailInputChange={setToEmailInput}
            onCcEmailInputChange={setCcEmailInput}
            onAddEmail={(section, email) => addEmailRecipient(section, email)}
            onRemoveEmail={removeEmailRecipient}
            onUpdateSignalUsers={(ids) => updateRecipientSection('to', 'signalUsers', ids)}
            onUpdateSignalGroups={(ids) => updateRecipientSection('to', 'signalGroups', ids)}
            recipientEmailSources={recipientEmailSources}
            implicitClientEmail={clientFallbackEmailOf({
                ...(change || {}),
                client: (selectedClient || change?.client) as { email?: string } | null,
            })}
            linkedClient={(() => {
                const primary = (selectedClient || change?.client) as Client | null | undefined;
                if (!primary)
                    return null;
                const primaryContacts = (primary as Client)?.contacts;
                if (Array.isArray(primaryContacts) && primaryContacts.length > 0)
                    return primary;
                const fallbackContacts = (change?.client as Client | undefined)?.contacts;
                if (Array.isArray(fallbackContacts) && fallbackContacts.length > 0)
                    return { ...primary, contacts: fallbackContacts } as Client;
                return primary;
            })()}
            memberOptions={replyRecipientMemberOptions}
            groupOptions={replyRecipientGroupOptions}
            isSaving={isSavingReplyRecipients}
            onSave={() => saveReplyRecipients()}
            className={replyRecipientsEditorAnchor === 'header'
                ? 'mt-2 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 p-3'
                : 'mt-3 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 p-3'}
        />
    ) : null;

    const toggleChangeNotifications = async () => {
        if (!change || isTogglingChangeNotifications)
            return;

        try {
            setIsTogglingChangeNotifications(true);
            if (change.hasSubscriber(user))
                await Changes.unsubscribeNotifications(change._id);
            else
                await Changes.subscribeNotifications(change._id);
            await dispatch(loadChange(change._id));
            await dispatch(loadProject(project._id));
        } finally {
            setIsTogglingChangeNotifications(false);
        }
    };

    const saveChangeSubscribers = async (subscriberIds: string[]) => {
        if (!change || isSavingSubscribers)
            return;

        try {
            setIsSavingSubscribers(true);
            await Changes.updateSubscribers(change._id, subscriberIds);
            await dispatch(loadChange(change._id));
        } finally {
            setIsSavingSubscribers(false);
        }
    };

    const refreshReplyRecipientsPreview = React.useCallback(async () => {
        if (!change || communicationTab !== 'reply')
            return;

        try {
            setReplyRecipientsPreviewError('');
            setIsLoadingReplyRecipientsPreview(true);
            const payload = {
                replyRecipients: normalizeRecipients(replyRecipients)
            };
            const response = await Changes.previewReplyRecipients(change._id, payload);
            setReplyRecipientsPreview(response.preview || null);
        } catch (error: unknown) {
            setReplyRecipientsPreview(null);
            setReplyRecipientsPreviewError(error instanceof Error ? error.message : (t('changes.reply_preview_failed') || ''));
        } finally {
            setIsLoadingReplyRecipientsPreview(false);
        }
    }, [change?._id, communicationTab, replyRecipients, t]);

    useEffect(() => {
        if (!show || !change)
            return;
        if (communicationTab !== 'reply') {
            setReplyRecipientsPreview(null);
            setReplyRecipientsPreviewError('');
            return;
        }
        refreshReplyRecipientsPreview();
    }, [show, change?._id, change?.updatedAt, communicationTab, replyRecipients, user?.updatedAt, refreshReplyRecipientsPreview]);

    const suggestReply = async () => {
        if (!change)
            return;

        // Freeze the insertion point / selection before the async call (button blur / typing).
        const insertionBase = replyEditorStateRef.current
            || (replyDraft ? moveSelectionToEnd(editorStateFromHtml(replyDraft)) : EditorState.createEmpty());
        const selectedPlain = selectedPlainText(insertionBase).trim();
        const isImproveSelection = !!selectedPlain;
        // Body-only: quoted citation lives in replyQuoteHtml outside Draft.
        const draftPlain = htmlToPlainText(replyDraft || '');
        const isImprove = isImproveSelection || !!draftPlain;
        const improveSource = isImproveSelection ? selectedPlain : draftPlain;

        try {
            setReplyError('');
            setReplySuccess('');
            setIsSuggestingReply(true);

            const comments = Array.isArray(change.comments) ? change.comments : [];
            const latestIncoming = [...comments].reverse().find((comment: AnyObj) => comment?.author?._id !== user?._id);
            const focusMessage = isImprove
                ? improveSource
                : ((replyDraft || '').trim() || (latestIncoming?.message || ''));
            const originalSummary = ((change as AnyObj).summary || '').trim();
            const contextualSummary = [
                originalSummary ? `Original summary:\n${originalSummary}` : '',
                focusMessage
                    ? (isImproveSelection
                        ? `Selected fragment to improve:\n${focusMessage}`
                        : (isImprove
                            ? `Rough draft to improve:\n${focusMessage}`
                            : `Current editor content (or latest received when editor is empty):\n${focusMessage}`))
                    : ''
            ].filter(Boolean).join('\n\n');
            const persona = [
                "Field meanings:",
                "- title: ticket title",
                "- description: ticket description",
                "- summary: original summary plus current editor content/fallback received message",
                "- draft: rough reply draft or selected fragment to improve when present",
                "- history: three previous messages ordered oldest to newest",
                isImproveSelection
                    ? "Improve only the selected fragment into polished text; put only that improved fragment in the reply field (not the full reply)."
                    : (isImprove
                        ? "Improve the draft into a polished reply; put only the improved reply body in the reply field."
                        : "Propose only the reply text to insert at the cursor in the reply field."),
                isImproveSelection
                    ? "Do not add greeting, closing, or signature; return only the polished fragment as plain text."
                    : "Format the reply field as plain text with blank lines between greeting, body paragraph(s), and closing (e.g. Cordialement).",
                "Never return the whole reply as a single line.",
                "Do not repeat content already present in the editor unless necessary.",
                "If a quoted citation is already present, leave it unchanged."
            ].join('\n');

            const response = await Changes.suggestReply({
                title: change.localizedTitle(i18n.language) || '',
                description: change.localizedDescription(i18n.language) || '',
                summary: contextualSummary,
                draft: isImprove ? improveSource : '',
                improveFragment: isImproveSelection,
                history: comments.slice(-3).map((comment: AnyObj) => ({
                    author: comment.authorName ? comment.authorName() : '',
                    message: comment.message
                })),
                persona
            });

            const suggestedBody = (response.reply || '').trim();
            if (!suggestedBody)
                return;

            // AI plain text often lacks \n; normalize structure then HTML for Draft.
            const suggestedHtml = formatSuggestedReplyHtml(suggestedBody);

            if (isImproveSelection) {
                syncReplyEditor(insertHtmlAtCursor(insertionBase, suggestedHtml));
                return;
            }

            if (isImprove) {
                // Replace body only; replyQuoteHtml stays attached out-of-band.
                syncReplyEditor(moveSelectionToEnd(editorStateFromHtml(suggestedHtml)));
                return;
            }

            syncReplyEditor(insertHtmlAtCursor(insertionBase, suggestedHtml));
        } catch (error: unknown) {
            setReplyError(error instanceof Error ? error.message : (t('changes.reply_suggest_failed') || ''));
        } finally {
            setIsSuggestingReply(false);
        }
    };

    const resolveProjectId = useCallback((): string | null => {
        const fromChange = change?.project;
        if (fromChange && typeof fromChange === 'object' && (fromChange as Project)._id)
            return (fromChange as Project)._id;
        if (typeof fromChange === 'string' && fromChange)
            return fromChange;
        return project?._id || null;
    }, [change?.project, project?._id]);

    const loadReplyTemplates = useCallback(async () => {
        const projectId = resolveProjectId();
        if (!projectId)
            return;
        setReplyTemplatesLoading(true);
        try {
            const json = await ReplyTemplates.list({ project: projectId });
            setReplyTemplates(json.reply_templates || []);
        } catch (_e) {
            setReplyTemplates([]);
        } finally {
            setReplyTemplatesLoading(false);
        }
    }, [resolveProjectId]);

    useEffect(() => {
        if (communicationComposerOpen && communicationTab === 'reply' && !isClientUser)
            loadReplyTemplates();
    }, [communicationComposerOpen, communicationTab, isClientUser, loadReplyTemplates]);

    const insertReplyTemplate = (template: ReplyTemplate) => {
        if (!change || !template)
            return;
        const projectName = (typeof change.project === 'object' && change.project?.name)
            || project?.name
            || '';
        const vars = ReplyTemplate.buildVars({
            slug: change.slug,
            title: change.localizedTitle(i18n.language) || '',
            projectName,
        });
        const rendered = ReplyTemplate.renderBody(template.body || '', vars).trim();
        insertHtmlIntoReply(rendered);
        setReplyError('');
        setReplySuccess('');
    };

    const sendReply = async (options: { skipAttachmentWordingCheck?: boolean } = {}) => {
        const plainReply = htmlToPlainText(replyDraft);
        if (!change || !plainReply)
            return;

        // Only the authored body — quoted history often contains « pièce jointe » / « attached ».
        if (!options.skipAttachmentWordingCheck && detectFalseAttachmentWording(replyDraft)) {
            setShowAttachmentWordingConfirm(true);
            return;
        }
        const outboundHtml = composeReplyMessage(replyDraft, replyQuoteHtml);
        setShowAttachmentWordingConfirm(false);

        try {
            setReplyError('');
            setReplySuccess('');
            setIsSendingReply(true);

            const response = await Changes.sendReply(change._id, {
                message: outboundHtml,
                replyRecipients: normalizeRecipients(replyRecipients)
            });
            setReplyRecipientsPreview(response.preview || null);
            await dispatch(loadChange(change._id));
            setReplySuccess(t('changes.reply_sent_success') || '');
            setReplyDraft('');
            setReplyQuoteHtml('');
            setReplyQuoteExpanded(false);
            setQuotedReplyAuthor(null);
            replyEditorStateRef.current = undefined;
            setReplyEditorState(undefined);
            setCommunicationComposerOpen(false);
        } catch (error: unknown) {
            setReplyError(error instanceof Error ? error.message : (t('changes.reply_send_failed') || ''));
        } finally {
            setIsSendingReply(false);
        }
    };

    const peelMergedReplyDraftIfNeeded = () => {
        if (replyQuoteHtml || !replyDraft)
            return;
        const peeled = peelLeadingReplyQuote(replyDraft);
        if (!peeled)
            return;
        setReplyQuoteHtml(peeled.quoteHtml);
        setReplyQuoteExpanded(false);
        setReplyDraft(peeled.bodyHtml);
        replyEditorStateRef.current = undefined;
        setReplyEditorState(undefined);
    };

    const openCommunicationComposer = (tab: 'reply' | 'comment') => {
        if (communicationComposerOpen && communicationTab === tab) {
            setCommunicationComposerOpen(false);
            return;
        }
        setCommunicationTab(tab);
        setCommunicationComposerOpen(true);
        setReplyError('');
        setReplySuccess('');
        if (tab === 'reply')
            peelMergedReplyDraftIfNeeded();
    };

    /** Discard reply/comment drafts and close the communication composer. */
    const cancelCommunicationComposer = () => {
        setReplyDraft('');
        setCommentDraft('');
        setReplyQuoteHtml('');
        setReplyQuoteExpanded(false);
        setQuotedReplyAuthor(null);
        replyEditorStateRef.current = undefined;
        setReplyEditorState(undefined);
        setReplyError('');
        setReplySuccess('');
        setCommunicationComposerOpen(false);
    };

    const handleQuoteFromTimeline = React.useCallback((payload: ReplyFromTimelinePayload) => {
        const quoted = buildQuotedCommunicationHtml(
            payload.authorLabel,
            t('changes.reply_quote_suffix') || '',
            payload.messageHtml
        );
        const target = (isClientUser || communicationTab === 'reply') ? 'email' : 'comment';
        if (target === 'email') {
            setCommunicationTab('reply');
            setCommunicationComposerOpen(true);
            setReplyError('');
            setReplySuccess('');
            // Keep citation out of Draft.js — body stays editable, quote is read-only below.
            setReplyQuoteHtml(normalizeHtmlForWysiwyg(quoted));
            setReplyQuoteExpanded(false);
            setQuotedReplyAuthor({
                displayName: quotedAuthorDisplayName(
                    { firstname: payload.authorFirstName, email: payload.authorEmail || undefined },
                    payload.authorLabel,
                ),
                email: payload.authorEmail || null,
                userId: payload.authorId || null,
            });
            // Empty body: seed two blank lines and put the caret at the start for typing.
            const bodySeed = htmlToPlainText(replyDraft) ? replyDraft : REPLY_LEADING_BLANK_HTML;
            syncReplyEditor(moveSelectionToStart(editorStateFromHtml(bodySeed)));
            requestAnimationFrame(() => {
                document.getElementById('change-modal-reply-wysiwyg-anchor')
                    ?.querySelector<HTMLElement>('[contenteditable="true"]')
                    ?.focus();
            });
            return;
        }
        setQuotedReplyAuthor(null);
        setCommunicationTab('comment');
        setCommunicationComposerOpen(true);
        const current = commentDraft || '';
        const stripped = htmlToPlainText(current);
        // Blanks first so the caret lands above the citation in Draft.
        const merged = stripped
            ? `${REPLY_LEADING_BLANK_HTML}${quoted}${current}`
            : `${REPLY_LEADING_BLANK_HTML}${quoted}`;
        setCommentDraft(normalizeHtmlForWysiwyg(merged));
    }, [t, communicationTab, isClientUser, replyDraft, commentDraft]);

    const isPendingTriage    = change?.progress?.status === Change.STATUS_PENDING_TRIAGE;
    const isEstimationStepOk = !isPendingTriage && (!change?.estimable || (change?.estimate > 0));
    // Non-dev: assign (and optional plan) without requiring estimate/deadline/isPlannable.
    const isNonDevCard = !!change && !change.isDev();
    const canShowPlanFields = !isPendingTriage && !!change && (
        isNonDevCard || (isEstimationStepOk && change.isPlannable())
    );
    const canShowAssignee = !isPendingTriage && !!change && (
        isNonDevCard || (isEstimationStepOk && !!change.deadline && change.isPlannable())
    );
    // Keep estimate/estimable beside deadline on non-dev (do not expand to full when estimate is missing).
    const estimateColSpan = (isEstimationStepOk || (isNonDevCard && canShowPlanFields)) ? '2' : 'full';
    const deadlineColSpan = change?.estimable
        ? (isEstimationStepOk ? 2 : 4)
        : 4;

    const remoteImageHtmlSources = React.useMemo(() => {
        if (!change)
            return [] as string[];
        const sources: string[] = [];
        const description = change.description as Record<string, string> | undefined;
        if (description && typeof description === 'object') {
            Object.values(description).forEach((value) => {
                if (typeof value === 'string' && value)
                    sources.push(value);
            });
        }
        (change.comments || []).forEach((comment: { message?: string; originalMessage?: string | null }) => {
            if (comment?.message)
                sources.push(comment.message);
            if (comment?.originalMessage)
                sources.push(comment.originalMessage);
        });
        return sources;
    }, [change?.description, change?.comments]);

    const content = React.useMemo(() => {
        if (!change)
            return null;

        const mr = change.getMergeRequest();
        const layoutExpanded = !!standalone || isExpanded;
        const compactHeader = layoutExpanded && !headerDetailsExpanded;
        const compactTitle = change.localizedTitle(i18n.language) || '';
        const compactRequesterEmails = requesterEmailsOf(change);
        const compactRequesterFallback = clientFallbackEmailOf({
            ...change,
            client: (selectedClient || change.client) as { email?: string } | null,
        });
        const compactMeta = compactHeaderMeta([
            change.author?.fullname?.() || '',
            change.createdAt ? formatMomentDate(change.createdAt, 'll') : '',
            showProjectName ? (change.project?.name || '') : (columnTitle || ''),
            compactRequesterEmails.join(', ') || compactRequesterFallback || '',
        ]);
        const headerDetailsLabel = headerDetailsExpanded
            ? (t('changes.header_details_collapse') || '')
            : (t('changes.header_details_expand') || '');
        const progressDetailHidden = layoutExpanded && progressDetailCollapsed;
        const pinComposerOutsideScroll = layoutExpanded && composerPinned;
        const composerPinToggle = layoutExpanded ? (
            <button
                type="button"
                className="flex items-center text-xs cursor-pointer font-normal border-none text-gray-500 dark:text-gray-400 hover:underline bg-transparent p-0"
                onClick={toggleComposerPinned}
                aria-pressed={composerPinned}
                aria-label={composerPinned
                    ? (t('changes.composer_unpin') || '')
                    : (t('changes.composer_pin') || '')}
                title={composerPinned
                    ? (t('changes.composer_unpin') || '')
                    : (t('changes.composer_pin') || '')}
            >
                <Icon
                    type="pin"
                    className={`inline mr-0.5 ${composerPinned ? 'text-amber-800 dark:text-amber-300' : ''}`}
                    size={3}
                    color="current"
                />
                <span>
                    {composerPinned
                        ? (t('changes.composer_unpin') || '')
                        : (t('changes.composer_pin') || '')}
                </span>
            </button>
        ) : null;
        return (
            <div className={`ChangeModal${layoutExpanded ? ' ChangeModal--expanded' : ''}${standalone ? ' ChangeModal--standalone flex flex-col lg:h-full lg:min-h-0 lg:flex-1' : ''}`}>
                <ChangeModalFrame
                    standalone={standalone}
                    onClose={handleModalClose}
                    dismissible={!mergeModalIsVisible && !pendingNonDevTypeFields}
                    isExpanded={layoutExpanded}
                    show={show}
                    sideOffset={sideOffset}
                    sideOffsetClass={sideOffsetClass}
                >
                    <Formik
                        innerRef={formikRef}
                        initialValues={change || {}}
                        enableReinitialize={true}
                        onSubmit={() => {}}
                    >
                        <RemoteImagesProvider changeId={change._id} htmlSources={remoteImageHtmlSources}>
                        <Form tabIndex={-1} onKeyDown={handleKeyPress} className={`bg-white dark:bg-gray-800 flex flex-col rounded-lg${layoutExpanded ? ` lg:max-h-full lg:min-h-0 lg:h-full lg:overflow-hidden${standalone ? ' lg:flex-1' : ''}` : ' max-h-full min-h-0 overflow-hidden'}`}>
                            <div className="shrink-0">
                            <ChangeModalHeader
                                standalone={standalone}
                                dense={compactHeader}
                                onClose={handleModalClose}
                                closeLabel={t('common.close') || t('changes.collapse_modal') || 'Close'}
                            >
                                <div className={compactHeader
                                    ? 'flex w-full min-w-0 items-center gap-2'
                                    : 'flex justify-start w-full items-start gap-2'}>
                                    {layoutExpanded && (
                                        <button
                                            type="button"
                                            className="inline-flex shrink-0 items-center rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                                            onClick={toggleHeaderDetails}
                                            aria-expanded={headerDetailsExpanded}
                                            aria-controls="change-modal-header-details"
                                            aria-label={headerDetailsLabel}
                                            title={headerDetailsLabel}
                                        >
                                            <Icon
                                                type={headerDetailsExpanded ? 'down' : 'right'}
                                                size={4}
                                                color="current"
                                            />
                                        </button>
                                    )}
                                    {!isCreation && (
                                        <div className={compactHeader
                                            ? 'flex shrink-0 items-center gap-1'
                                            : 'flex flex-col items-start gap-1 mr-1.5 shrink-0'}>
                                            <span className={`flex flex-col justify-center items-center font-semibold bg-cyan-100 text-cyan-800 dark:bg-cyan-200 dark:text-cyan-800 group-hover:bg-cyan-200 dark:group-hover:bg-cyan-300 rounded text-xs w-fit ${compactHeader ? 'px-1.5 py-0.5' : 'p-2'}`}>
                                                <span className="max-w-[90px] truncate">
                                                    <CopyButton title={ change.slugName() } textToCopy={ change.slugName() } color="none" hover />
                                                </span>
                                            </span>
                                            <ClientReplyPendingBadge change={change} dismissable />
                                        </div>
                                    )}
                                    <div
                                        id="change-modal-header-details"
                                        className={compactHeader
                                            ? 'flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1'
                                            : 'flex flex-col justify-center w-full min-w-0'}
                                    >
                                        {compactHeader ? (
                                            <span
                                                className="min-w-[6rem] flex-1 truncate text-sm font-semibold text-gray-900 dark:text-white"
                                                title={compactTitle}
                                            >
                                                {compactTitle}
                                            </span>
                                        ) : (
                                        <div className="m-0 p-0 w-full">
                                            <TranslatableField
                                                as="input"
                                                type="text"
                                                name="title"
                                                hideEditLink
                                                readOnly={!user?.can('change:title:edit')}
                                                className="font-bold"
                                                onChange={ onChangeField }
                                                onSave={ onSaveField }
                                                langs={project?.translation?.languages?.all}
                                                required
                                            />
                                            <ErrorMessage name="title" component="div" />
                                        </div>
                                        )}
                                        {compactHeader && compactMeta && (
                                            <span
                                                className="min-w-0 max-w-[40%] shrink truncate text-xs font-normal not-italic text-gray-500 dark:text-gray-400"
                                                title={compactMeta}
                                            >
                                                {compactMeta}
                                            </span>
                                        )}
                                        {!compactHeader && (
                                        <div className="flex flex-row justify-between w-full">
                                            {showProjectName ? (
                                                <small className="flex items-center space-x-2 font-normal text-sm italic leading-6 w-full pl-1 -mt-1">
                                                    <UserAvatar user={change.author as User} size={5} hideInitials />
                                                    <a
                                                        href={Project.path(change?.project, 'change', change?._id)}
                                                        className="focus:outline-none"
                                                        dangerouslySetInnerHTML={{__html: createdByAndAt() + ' ' + t('changes.in_the_project', { project: change.project?.name, }) || ''}}
                                                    />
                                                </small>

                                            ) : (
                                                <small className="flex items-center space-x-2 font-normal text-sm italic leading-6 w-full pl-1 -mt-1">
                                                    <UserAvatar user={change.author as User} size={5} hideInitials />
                                                    <span dangerouslySetInnerHTML={{__html: createdByAndAt() + ' ' + t('changes.in_the_column', {column: columnTitle}) || ''}}
                                                    />
                                                </small>
                                            )}
                                        </div>
                                        )}
                                        {!compactHeader && (() => {
                                            const headerDemandeurs = requesterEmailsOf(change);
                                            const headerClientFallback = clientFallbackEmailOf({
                                                ...change,
                                                client: (selectedClient || change.client) as { email?: string } | null,
                                            });
                                            const showHeaderRecipients = !isClientUser
                                                || headerDemandeurs.length > 0
                                                || !!headerClientFallback
                                                || ccEmailsOf(change).length > 0;
                                            if (!showHeaderRecipients)
                                                return null;
                                            return (
                                            <div className="pl-1 mt-0.5 text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <span className="font-medium text-gray-500 dark:text-gray-400">
                                                        {t('requester.title')}:
                                                    </span>
                                                    {headerDemandeurs.length > 0 ? (
                                                        <RequesterEmails
                                                            emails={headerDemandeurs}
                                                            className="text-xs text-gray-700 dark:text-gray-200"
                                                            linkClassName="text-indigo-600 dark:text-indigo-200 hover:underline"
                                                        />
                                                    ) : headerClientFallback ? (
                                                        <>
                                                            <RequesterEmails
                                                                emails={[headerClientFallback]}
                                                                className="text-xs text-gray-700 dark:text-gray-200"
                                                                linkClassName="text-indigo-600 dark:text-indigo-200 hover:underline"
                                                            />
                                                            <span className="text-gray-400 italic">
                                                                ({t('requester.via_client')})
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    )}
                                                    {!isClientUser && (
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-indigo-600 hover:underline bg-transparent border-none p-0 cursor-pointer"
                                                            onClick={() => toggleReplyRecipientsEditor('header')}
                                                            title={t('changes.reply_recipients_edit') || ''}
                                                        >
                                                            <Icon
                                                                type={replyRecipientsEditorVisible && replyRecipientsEditorAnchor === 'header' ? 'close' : 'edit'}
                                                                size={3}
                                                                className="inline"
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                                {(ccEmailsOf(change).length > 0
                                                    || (replyRecipientsEditorVisible && replyRecipientsEditorAnchor === 'header')) && (
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        <span className="font-medium text-gray-500 dark:text-gray-400">
                                                            {t('changes.reply_recipients_cc')}:
                                                        </span>
                                                        {ccEmailsOf(change).length > 0 ? (
                                                            <RequesterEmails
                                                                emails={ccEmailsOf(change)}
                                                                className="text-xs text-gray-700 dark:text-gray-200"
                                                                linkClassName="text-indigo-600 dark:text-indigo-200 hover:underline"
                                                            />
                                                        ) : (
                                                            <span className="text-xs text-gray-400">—</span>
                                                        )}
                                                    </div>
                                                )}
                                                {replyRecipientsEditorAnchor === 'header' && replyRecipientsEditorNode}
                                                {replyRecipientsEditorAnchor === 'header' && replyRecipientsDirty && (
                                                    <div className="text-xs text-amber-700">
                                                        {t('changes.reply_recipients_unsaved')}
                                                    </div>
                                                )}
                                                {replyRecipientsEditorAnchor === 'header' && replyRecipientsError && (
                                                    <Alert title={replyRecipientsError} icon="warning" color="failure" />
                                                )}
                                                {replyRecipientsEditorAnchor === 'header' && replyRecipientsSuccess && (
                                                    <Alert title={replyRecipientsSuccess} icon="check" color="success" />
                                                )}
                                            </div>
                                            );
                                        })()}
                                        <div className={compactHeader ? 'contents' : 'flex flex-col gap-2 mt-2'}>
                                            <div className={compactHeader ? 'order-last flex w-full flex-col gap-2' : 'contents'}>
                                            {deleteError && (
                                                <Alert
                                                    color="failure"
                                                    icon="warning"
                                                    title={deleteError}
                                                />
                                            )}
                                            {change.progress?.status === Change.STATUS_DISCARDED && (
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <Alert
                                                        color="warning"
                                                        icon="archive"
                                                        title={t('changes.in_trash_banner')}
                                                    />
                                                    {userCanDeleteThisCard && (
                                                        <Button
                                                            small
                                                            color="primary"
                                                            icon="unarchive"
                                                            title={t('changes.restore') as string}
                                                            onClick={handleRestore}
                                                            loading={updateStatus === 'pending'}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {change.isMerged() && mergedTargetPath && (
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <Alert
                                                        color="warning"
                                                        icon="merge"
                                                        title={t('changes.merged_into', { slug: change.mergedInto?.slug ? `#${change.mergedInto.slug}` : '' })}
                                                    />
                                                    <a className="text-sm font-medium underline" href={mergedTargetPath}>
                                                        {t('changes.open_merge_target')}
                                                    </a>
                                                </div>
                                            )}
                                            {mergeError && !mergeModalIsVisible && (
                                                <Alert title={mergeError} icon="warning" color="failure" />
                                            )}
                                            </div>
                                            <div className={compactHeader
                                                ? 'flex shrink-0 items-center gap-2'
                                                : 'flex flex-row items-center gap-2 mt-2 w-full min-w-0'}>
                                            <div className="flex flex-row items-center gap-2 shrink-0">
                                            <span title={change.hasSubscriber(user) ? (t('changes.notifications_unsubscribe') || '') : (t('changes.help.subscribe') || t('changes.notifications_subscribe') || '')}>
                                                <Button
                                                    title=""
                                                    icon={change.hasSubscriber(user) ? "reminder" : "reminderOff"}
                                                    iconSize={4}
                                                    color={change.hasSubscriber(user) ? "secondary" : "warning"}
                                                    small
                                                    loading={isTogglingChangeNotifications}
                                                    onClick={() => toggleChangeNotifications()}
                                                />
                                            </span>
                                            <div
                                                className="flex h-6 flex-row items-center gap-0.5 rounded border border-gray-300 dark:border-gray-600 px-1"
                                                title={t('changes.subscribers') || ''}
                                            >
                                                <div className="flex -space-x-1">
                                                    {subscriberCandidates
                                                        .filter((candidate: User) => (change.subscribers || []).includes(candidate._id))
                                                        .slice(0, 5)
                                                        .map((candidate: User) => (
                                                            <UserAvatar
                                                                key={candidate._id}
                                                                user={candidate}
                                                                size={4}
                                                                hideInitials
                                                            />
                                                        ))}
                                                </div>
                                                <Dropdown
                                                    label=""
                                                    placement="bottom"
                                                    dismissOnClick={false}
                                                    renderTrigger={() => (
                                                        <button
                                                            type="button"
                                                            title={t('changes.subscribers_edit') || ''}
                                                            disabled={isSavingSubscribers}
                                                            className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 disabled:opacity-50"
                                                        >
                                                            <Icon
                                                                type={isSavingSubscribers ? 'loading' : 'plus'}
                                                                size={3}
                                                                color="current"
                                                            />
                                                        </button>
                                                    )}
                                                >
                                                    <div className="w-64 p-3">
                                                        <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                            {t('changes.subscribers')}
                                                        </div>
                                                        <div className="max-h-56 space-y-0.5 overflow-y-auto">
                                                            {subscriberCandidates.length === 0 && (
                                                                <p className="px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400">
                                                                    {t('changes.subscribers_placeholder')}
                                                                </p>
                                                            )}
                                                            {subscriberCandidates.map((candidate: User) => {
                                                                const checked = (change.subscribers || []).includes(candidate._id);
                                                                return (
                                                                    <button
                                                                        key={candidate._id}
                                                                        type="button"
                                                                        disabled={isSavingSubscribers}
                                                                        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 disabled:opacity-50 ${
                                                                            checked ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-900' : 'text-gray-900 dark:text-gray-100'
                                                                        }`}
                                                                        onClick={() => {
                                                                            const current = change.subscribers || [];
                                                                            const next = checked
                                                                                ? current.filter((id: string) => id !== candidate._id)
                                                                                : [...current, candidate._id];
                                                                            saveChangeSubscribers(next);
                                                                        }}
                                                                    >
                                                                        <UserAvatar user={candidate} size={5} hideInitials />
                                                                        <span className="min-w-0 flex-1 truncate">
                                                                            {candidate.fullname?.() || candidate.email || candidate._id}
                                                                        </span>
                                                                        {checked && (
                                                                            <Icon type="check" size={3} color="blue-600" />
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </Dropdown>
                                            </div>
                                            {change.isDev() && mr && (
                                                <Button
                                                    title=""
                                                    icon="gitlab"
                                                    iconSize={4}
                                                    color="secondary"
                                                    small
                                                    to={ mr.link }
                                                    target="_blank"
                                                />
                                            )}
                                            {change?.issue?.id && (
                                                <Button
                                                    title={ t(`changes.badge.see_issue_on_${change.issue.source}`) }
                                                    icon={ change.issueIconName() }
                                                    iconSize={3}
                                                    color="warning"
                                                    small
                                                    to={change.issue.link}
                                                    target="_blank"
                                                />
                                            )}
                                            <Ability can="change:ticket:edit">
                                                <div className="group flex items-center">
                                                    <Badge color="indigo" className={`w-fit py-1 ${change.ticket_number ? 'group-hover:rounded-r-none' : ''}`}>
                                                        <div className="flex flex-row items-center gap-0.5">
                                                            <Icon type="ticket" color="current" size={3} />
                                                            <InlineField
                                                                as="input"
                                                                type="text"
                                                                name="ticket_number"
                                                                placeholder="123456"
                                                                onChange={ onChangeField }
                                                                onSave={ onSaveField }
                                                                isLoading={ updating === 'pending' }
                                                                readOnly={ !user?.can('change:title:edit') }
                                                                className="w-[50px] text-right text-xs bg-transparent text-inherit placeholder:text-indigo-400 dark:placeholder:text-indigo-700"
                                                            />
                                                        </div>
                                                    </Badge>
                                                    {change.ticket_number && (
                                                        <>
                                                            <Button
                                                                title=""
                                                                icon="eye"
                                                                iconSize={4}
                                                                color="navigateBack"
                                                                small
                                                                to={change.ticket_view_url}
                                                                target="_blank"
                                                                className="rounded-none hidden group-hover:inline-flex"
                                                            />
                                                            <Button
                                                                title=""
                                                                icon="reply"
                                                                iconSize={4}
                                                                color="navigateBack"
                                                                small
                                                                to={change.ticket_reply_url}
                                                                target="_blank"
                                                                className="rounded-none hidden group-hover:inline-flex"
                                                            />
                                                            <Button
                                                                title=""
                                                                icon="close"
                                                                iconSize={4}
                                                                color="navigateBack"
                                                                small
                                                                to={change.ticket_close_url}
                                                                target="_blank"
                                                                className="rounded-l-none hidden group-hover:inline-flex"
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </Ability>
                                            <Tooltip content={t('changes.help.priority') || ''} light>
                                                <Badge color="indigo" className="w-fit py-1">
                                                    <Icon type="priority" color="current" className="inline mr-1" />
                                                    {change.priority}
                                                </Badge>
                                            </Tooltip>
                                            </div>
                                            {project && !isPendingTriage && (
                                                <div className={compactHeader ? 'min-w-0 max-w-[12rem] shrink [&>div]:!ml-0 [&>div]:!max-w-full' : 'contents'}>
                                                    <ChangeLabelsField
                                                        change={change}
                                                        project={project}
                                                        readOnly={!user?.can('change:edit')}
                                                        onSave={onSaveField}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                    {user?.can('track:create') && change.assignee?._id === user._id && change.isTimeTrackable() && (
                                        <span className="flex items-center font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:bg-blue-200 dark:text-blue-800 group-hover:bg-blue-200 dark:group-hover:bg-blue-300 rounded p-2 text-xs w-fit text-xs">
                                            <ChangeTrack change={change} />
                                        </span>
                                    )}
                                    {onTogglePin && (
                                        <Tooltip content={isPinned ? (t('changes.unpin') || '') : (t('changes.pin') || '')} light>
                                            <button
                                                type="button"
                                                className={`ml-1 inline-flex shrink-0 items-center rounded-lg p-1.5 text-sm hover:bg-gray-200 dark:hover:bg-gray-600 ${
                                                    isPinned
                                                        ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200'
                                                        : 'bg-transparent text-gray-400 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
                                                }`}
                                                onClick={onTogglePin}
                                                aria-label={isPinned ? (t('changes.unpin') || '') : (t('changes.pin') || '')}
                                                aria-pressed={!!isPinned}
                                            >
                                                <Icon type="pin" size={5} color="current" />
                                            </button>
                                        </Tooltip>
                                    )}
                                    {!standalone && (
                                        <Tooltip content={t('changes.open_in_new_tab') || ''} light>
                                            <button
                                                type="button"
                                                className="ml-1 inline-flex shrink-0 items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                                                onClick={() => {
                                                    const path = Project.path(project || change?.project, 'change', changeId || change?._id);
                                                    if (path)
                                                        window.open(path, '_blank', 'noopener,noreferrer');
                                                }}
                                                aria-label={t('changes.open_in_new_tab') || ''}
                                            >
                                                <Icon type="external" size={5} color="current" />
                                            </button>
                                        </Tooltip>
                                    )}
                                    {!standalone && (
                                        <Tooltip content={isExpanded ? (t('changes.collapse_modal') || '') : (t('changes.expand_modal') || '')} light>
                                            <button
                                                type="button"
                                                className="ml-1 inline-flex shrink-0 items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                                                onClick={toggleExpanded}
                                                aria-label={isExpanded ? (t('changes.collapse_modal') || '') : (t('changes.expand_modal') || '')}
                                                aria-pressed={isExpanded}
                                            >
                                                <Icon type={isExpanded ? 'compress' : 'expand'} size={5} color="current" />
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                            </ChangeModalHeader>
                            </div>
                            {!change.isMerged() && (change.related || []).length > 0 && (
                                <div className="flex items-start justify-between rounded-t dark:border-gray-600 border-b p-5 py-3 w-full">
                                    <div className="text-xl font-medium text-gray-900 dark:text-white w-full">
                                        <div className="flex justify-start w-full">
                                            <div className="flex flex-col justify-center w-full">
                                                <div className="flex flex-col gap-2 mt-2">
                                                <div className="rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2">
                                                    <div className={`flex items-center justify-between gap-2 ${relatedExpanded ? 'mb-2' : ''}`}>
                                                        <button
                                                            type="button"
                                                            className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500 hover:text-gray-700 dark:text-gray-200"
                                                            onClick={toggleRelatedExpanded}
                                                            aria-expanded={relatedExpanded}
                                                            aria-controls="change-modal-related-cards"
                                                            title={(relatedExpanded
                                                                ? t('changes.related_collapse')
                                                                : t('changes.related_expand')) || ''}
                                                        >
                                                            <Icon
                                                                type={relatedExpanded ? 'down' : 'right'}
                                                                size={3}
                                                                color="gray-500"
                                                            />
                                                            <span>{t('changes.related_cards')}</span>
                                                            <span className="rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-gray-600 dark:text-gray-300">
                                                                {(change.related || []).length}
                                                            </span>
                                                        </button>
                                                        <Button
                                                            small
                                                            color="none"
                                                            textColor="gray-500"
                                                            title={t('changes.related_add') as string}
                                                            icon="plus"
                                                            iconColor="gray-500"
                                                            onClick={() => setRelatedModalVisible(true)}
                                                            disabled={isLinkingRelated}
                                                        />
                                                    </div>
                                                    {relatedExpanded && (
                                                    <div id="change-modal-related-cards" className="space-y-1">
                                                        {(change.related || []).map((related: Change) => {
                                                            const relatedPath = Project.path(
                                                                related.project || change.project,
                                                                'change',
                                                                related._id,
                                                            );
                                                            const relatedStatus = related.progress?.status;
                                                            const relatedStatusLabel = relatedStatus
                                                                ? (t(`changes.progress_status_type.${relatedStatus}`) || relatedStatus)
                                                                : '';
                                                            const relatedIsFinished = related.isFinished?.() || related.isMerged?.();
                                                            const relatedIsInReview = related.isInReview?.();
                                                            return (
                                                                <div
                                                                    key={related._id}
                                                                    className="flex items-center gap-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
                                                                >
                                                                    <a
                                                                        href={relatedPath}
                                                                        className="flex min-w-0 flex-1 items-center gap-2 hover:underline"
                                                                    >
                                                                        <span className="shrink-0 rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold text-indigo-800">
                                                                            {related.slugName?.() || related.slug}
                                                                        </span>
                                                                        <span className="truncate text-gray-800 dark:text-gray-100">
                                                                            {related.localizedTitle?.(i18n.language) || related.title?.fr || related._id}
                                                                        </span>
                                                                        {relatedStatusLabel && (
                                                                            <Tooltip content={relatedStatusLabel} light>
                                                                                <span
                                                                                    className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                                                                        relatedIsFinished
                                                                                            ? 'bg-green-50 dark:bg-green-950/30 text-green-700'
                                                                                            : relatedIsInReview
                                                                                                ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700'
                                                                                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                                                                                    }`}
                                                                                >
                                                                                    {relatedIsFinished && (
                                                                                        <Icon type="check" size={3} color="green-600" />
                                                                                    )}
                                                                                    {relatedIsInReview && !relatedIsFinished && (
                                                                                        <Icon type="eye" size={3} color="blue-600" />
                                                                                    )}
                                                                                    {relatedStatusLabel}
                                                                                </span>
                                                                            </Tooltip>
                                                                        )}
                                                                        {related.project?.name && (
                                                                            <span className="ml-auto shrink-0 text-[11px] text-gray-400">
                                                                                {related.project.name}
                                                                            </span>
                                                                        )}
                                                                    </a>
                                                                    <button
                                                                        type="button"
                                                                        className="shrink-0 text-gray-400 hover:text-red-600"
                                                                        title={t('changes.related_unlink') || ''}
                                                                        onClick={() => unlinkRelated(related._id)}
                                                                        disabled={isLinkingRelated}
                                                                    >
                                                                        <Icon type="close" size={3} />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    )}
                                                </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <ChangeModalBody standalone={standalone} isExpanded={layoutExpanded}>
                                <Transition
                                    as={Fragment}
                                    appear={true}
                                    show={!!change}
                                    enter="transition ease-out duration-1000"
                                    enterFrom="transform opacity-0"
                                    enterTo="transform opacity-100"
                                >
                                    <div className={layoutExpanded ? 'flex flex-col lg:h-full lg:min-h-0' : '-mt-5'}>
                                        <div className="w-fit py-1 absolute top-1 right-0 z-20 flex items-center" >
                                            <Dropdown label="" placement="bottom" dismissOnClick={false} renderTrigger={() => (
                                                <div className="cursor-pointer">
                                                    <Icon type="menu" color="current" className="hover:bg-gray-200 dark:hover:bg-gray-700 p-1 rounded text-gray-700 dark:text-gray-200" size={6} />
                                                </div>
                                            )}>
                                                <Dropdown.Item className="font-medium text-xs w-48" onClick={() => addToDoList()}>
                                                    <Icon type="list" color="current" className="mr-1.5" size={3} />
                                                    {t('changes.checklist.add_a_checklist')}
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                                {!change?.isMerged?.() && (
                                                    <Dropdown.Item className="font-medium text-xs w-48" onClick={openMergeModal}><Icon type="merge" color="current" className="mr-1.5" size={3} />{t('changes.merge_this_change')}</Dropdown.Item>
                                                )}
                                                <Dropdown.Item className="font-medium text-xs w-48" onClick={() => setRelatedModalVisible(true)}><Icon type="branch" color="current" className="mr-1.5" size={3} />{t('changes.link_this_change')}</Dropdown.Item>
                                                {canSpawnLinkedChange(change) && (
                                                    <Dropdown.Item
                                                        className="font-medium text-xs w-48"
                                                        disabled={isCreatingLinked}
                                                        onClick={() => createLinkedChange()}
                                                    >
                                                        <Icon type="branch" color="current" className="mr-1.5" size={3} />
                                                        {t('changes.create_linked_change')}
                                                    </Dropdown.Item>
                                                )}
                                                <Dropdown.Item className="font-medium text-xs w-48" onClick={() => showMoveModal(true)}>
                                                    <Icon type="move" color="current" className="mr-1.5" size={3} />
                                                    {t('changes.move_this_change')}
                                                </Dropdown.Item>
                                                {userCanDeleteThisCard && change?.progress?.status === Change.STATUS_DISCARDED && (
                                                    <>
                                                        <Dropdown.Divider />
                                                        <Dropdown.Item
                                                            className="font-medium text-xs w-48"
                                                            onClick={handleRestore}
                                                        >
                                                            <Icon type="unarchive" color="current" className="mr-1.5" size={3} />
                                                            {t('changes.restore')}
                                                        </Dropdown.Item>
                                                    </>
                                                )}
                                                {userCanDeleteThisCard && change?.progress?.status !== Change.STATUS_DISCARDED && (
                                                    <>
                                                        <Dropdown.Divider />
                                                        <Dropdown.Item onClick={() => handleDiscard(change)} className="font-medium text-xs text-red-500">
                                                            <Icon type="delete" className="mr-1.5" color="current" size={3} />
                                                            {t('changes.delete')}
                                                        </Dropdown.Item>
                                                    </>
                                                )}
                                            </Dropdown>
                                        </div>
                                        {/*
                                          Flowbite Tabs copies `className` onto the tablist as well as the root.
                                          Never put flex-col / flex-1 on Tabs className — it stacks the tabs vertically.
                                        */}
                                        <div className={layoutExpanded ? 'flex flex-col lg:min-h-0 lg:flex-1 lg:overflow-hidden' : undefined}>
                                        <Tabs
                                            theme={{
                                                ...(layoutExpanded ? {
                                                    base: 'flex flex-col gap-2 lg:h-full lg:min-h-0',
                                                    // Desktop: clip here; each panel / the activity column owns scrolling.
                                                    // overflow-y-auto on this container broke nested timeline scroll
                                                    // (content taller than the pane could neither scroll here nor inside).
                                                    // MultiSelect options are portaled, so overflow-hidden is safe.
                                                    // Mobile: natural height — only the App outlet scrolls.
                                                    tabitemcontainer: { base: 'flex flex-col lg:min-h-0 lg:flex-1 lg:overflow-hidden' },
                                                    tabpanel: 'py-3 [&:not([hidden])]:flex [&:not([hidden])]:flex-col lg:h-full lg:min-h-0 lg:flex-1 lg:overflow-hidden',
                                                } : {}),
                                                tablist: {
                                                    tabitem: {
                                                        base: `flex items-center justify-center ${layoutExpanded ? 'px-3 py-2' : 'p-4'} rounded-t-lg text-sm font-medium first:ml-0 disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500 focus:outline-none focus:ring-0`,
                                                    },
                                                },
                                            }}
                                        >
                                            <Tabs.Item icon={() => <Icon type="list" color="current" className="mr-1.5 outline-none" />} title={t('changes.step_progress')}>
                                                <div className={layoutExpanded
                                                    ? (progressDetailHidden
                                                        // Keep CSS grid when collapsed so the activity column still gets a
                                                        // definite 1fr track (switching to flex collapsed height/width to 0).
                                                        ? 'relative grid h-full min-h-0 flex-1 grid-cols-[auto_minmax(0,1fr)] grid-rows-[minmax(0,1fr)] overflow-hidden'
                                                        // Below lg: stack with natural height (single App scroll). lg+:
                                                        // side-by-side panes with internal scroll.
                                                        : 'relative grid grid-cols-1 lg:h-full lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden')
                                                    : 'grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6'
                                                }>
                                                    <div className={
                                                        progressDetailHidden
                                                            ? 'hidden'
                                                            : (layoutExpanded ? 'min-h-0 pr-3 lg:overflow-y-auto' : 'contents')
                                                    }>
                                                        <div className={layoutExpanded ? 'grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6' : 'contents'}>
                                                    <div className="col-span-full">
                                                        {(() => {
                                                            const typeDisplay = getChangeTypeDisplay(change, t);
                                                            const canEditType = !!user?.can('change:type:edit');
                                                            const canEditClient = !!user?.can('change:client:edit');
                                                            const canEditImportance = !!user?.can('change:importance:edit');
                                                            const metaChipClass = (active: boolean, editable: boolean) =>
                                                                `inline-flex items-center gap-1.5 max-w-full rounded-md border px-2 py-1 text-xs ${
                                                                    active
                                                                        ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200'
                                                                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100'
                                                                } ${editable ? 'hover:bg-slate-100 dark:hover:bg-gray-700 cursor-pointer' : 'cursor-default'}`;

                                                            return (
                                                                <div className="space-y-2">
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <button
                                                                            type="button"
                                                                            disabled={!canEditType}
                                                                            className={metaChipClass(editingMeta === 'type', canEditType)}
                                                                            onClick={() => setEditingMeta(editingMeta === 'type' ? null : 'type')}
                                                                            title={t('changes.meta_type') || ''}
                                                                        >
                                                                            <Icon type={typeDisplay.icon} size={3} color="current" />
                                                                            <span className="truncate max-w-[12rem]">{typeDisplay.label}</span>
                                                                            {canEditType && <Icon type="down" size={3} color="current" className="opacity-60" />}
                                                                        </button>
                                                                        {(() => {
                                                                            const client = selectedClient || change.client;
                                                                            const clientId = client && !_.isString(client) ? client._id : undefined;
                                                                            const clientLabel = (() => {
                                                                                if (!client)
                                                                                    return t('changes.meta_no_client') || '';
                                                                                if (_.isString(client))
                                                                                    return client;
                                                                                if (typeof client.name === 'function')
                                                                                    return client.name();
                                                                                return [client.id, client.company].filter(Boolean).join(' - ');
                                                                            })();

                                                                            if (!canEditClient && !clientId)
                                                                                return null;

                                                                            if (!canEditClient && clientId) {
                                                                                return (
                                                                                    <Link
                                                                                        to={`/clients/${clientId}`}
                                                                                        className={metaChipClass(false, true)}
                                                                                        title={t('client_view.open_fiche') || ''}
                                                                                    >
                                                                                        <Icon type="client" size={3} color="current" />
                                                                                        <span className="truncate max-w-[16rem]">{clientLabel}</span>
                                                                                    </Link>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <span className="inline-flex items-center gap-1 max-w-full">
                                                                                    <button
                                                                                        type="button"
                                                                                        className={metaChipClass(editingMeta === 'client', true)}
                                                                                        onClick={() => {
                                                                                            ensureClientsLoaded();
                                                                                            setEditingMeta(editingMeta === 'client' ? null : 'client');
                                                                                        }}
                                                                                        title={t('changes.meta_client') || ''}
                                                                                    >
                                                                                        <Icon type="client" size={3} color="current" />
                                                                                        <span className="truncate max-w-[16rem]">{clientLabel}</span>
                                                                                        <Icon type="down" size={3} color="current" className="opacity-60" />
                                                                                    </button>
                                                                                    {clientId && (
                                                                                        <Link
                                                                                            to={`/clients/${clientId}`}
                                                                                            className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-1.5 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700"
                                                                                            title={t('client_view.open_fiche') || ''}
                                                                                        >
                                                                                            <Icon type="eye" size={3} color="current" />
                                                                                        </Link>
                                                                                    )}
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                        {canEditImportance && (
                                                                            <span className="inline-flex items-center gap-1">
                                                                                <button
                                                                                    type="button"
                                                                                    className={metaChipClass(editingMeta === 'importance', true)}
                                                                                    onClick={() => setEditingMeta(editingMeta === 'importance' ? null : 'importance')}
                                                                                    title={t('changes.importance') || ''}
                                                                                >
                                                                                    <Icon type="importance" size={3} color="current" />
                                                                                    <span className="truncate max-w-[14rem]">
                                                                                        {t(`changes.importance_value_${change.importance}`) || t('changes.importance')}
                                                                                    </span>
                                                                                    <Icon type="down" size={3} color="current" className="opacity-60" />
                                                                                </button>
                                                                                <FieldHelp content={t('changes.help.importance') || ''} />
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {editingMeta === 'type' && (
                                                                        <div className="border bg-slate-50 dark:bg-gray-800 p-2 rounded">
                                                                            <TypeField
                                                                                name="type"
                                                                                readOnly={!canEditType}
                                                                                typeDomain={change?.typeDomain}
                                                                                typeSubtype={change?.typeSubtype}
                                                                                onSave={(field, value) => {
                                                                                    onSaveField(field, value);
                                                                                    setEditingMeta(null);
                                                                                }}
                                                                                onSaveMultiple={onSaveTypeFields}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {editingMeta === 'client' && canEditClient && (
                                                                        <div className="border bg-slate-50 dark:bg-gray-800 p-2 rounded">
                                                                            <ClientSelectField
                                                                                name="client"
                                                                                placeholder={t('projects.search_client') || ''}
                                                                                onChange={(client) => {
                                                                                    setClientChanged(true);
                                                                                    setSelectedClient(client);
                                                                                    onSaveField('client', client ?? null);
                                                                                    if (client)
                                                                                        setEditingMeta(null);
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {editingMeta === 'importance' && canEditImportance && (
                                                                        <div className="border bg-slate-50 dark:bg-gray-800 p-2 rounded">
                                                                            <ImportanceField
                                                                                name="importance"
                                                                                className="border p-0.5 rounded-md"
                                                                                placeholder={t('changes.importance') || ''}
                                                                                onChange={importance => {
                                                                                    onSaveField('importance', importance);
                                                                                    setEditingMeta(null);
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                    <Ability can="change:description:edit">
                                                        <div className="col-span-full">
                                                            <TranslatableWysiwygField
                                                                name="description"
                                                                inline
                                                                langs={project?.translation?.languages?.all}
                                                                placeholder={t('changes.enter_description') || ''}
                                                                rows={4}
                                                                label={(
                                                                    <LabelWithHelp htmlFor="description" help={t('changes.help.description') || ''} className="block">
                                                                        <Icon type="description" color="current" className="inline mr-1.5" />
                                                                        {t('changes.description')}
                                                                    </LabelWithHelp>
                                                                )}
                                                                isLoading={ updating === 'pending' }
                                                                onChange={ onChangeField }
                                                                onSave={ onSaveField }
                                                                uploadProps={{
                                                                    projectId: project?._id,
                                                                    changeId: change?._id
                                                                }}
                                                            />
                                                        </div>
                                                    </Ability>
                                                    <Ability can="change:notes:edit">
                                                        <TechnicalContextPanel change={change} onMergeReview={change.technicalContext?.mergeReview?.candidateId ? mergeIntoCandidate : undefined} />
                                                    </Ability>
                                                    {!isPendingTriage && (
                                                        <div className={`col-span-full md:col-span-${estimateColSpan} flex flex-col items-center gap-x1 border border-gray-100 dark:border-gray-700 p-2 ${
                                                            isEstimationStepOk
                                                                ? 'bg-gray-50 dark:bg-gray-900'
                                                                : 'bg-red-50 dark:bg-red-950/40'
                                                        }`}>
                                                            {change.estimable && (
                                                                <>
                                                                    <LabelWithHelp htmlFor="estimate" help={t('changes.help.estimate') || ''} className="mr-2">
                                                                        {t('changes.estimate_desc')}
                                                                    </LabelWithHelp>
                                                                    <InlineField
                                                                        as="input"
                                                                        type="number"
                                                                        step={0.5}
                                                                        name="estimate"
                                                                        onChange={ onChangeField }
                                                                        onSave={ onSaveField }
                                                                        isLoading={ updating === 'pending' }
                                                                        readOnly={ !user?.can('change:estimate') }
                                                                        className={`arrow-hide w-10 text-center -mt-1 font-bold text-xl mr-2 w-full ${
                                                                            isEstimationStepOk
                                                                                ? 'bg-gray-50 dark:bg-gray-900'
                                                                                : 'bg-red-50 dark:bg-red-950/40'
                                                                        }`}
                                                                    />
                                                                </>
                                                            )}
                                                            { change.estimate > 0 && (
                                                            <label htmlFor="estimate" className="flex-initial text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                {t('changes.estimate_units')}
                                                            </label>
                                                            )}
                                                            {!change.isDev() && (change.estimate === 0) && !change.billable && (
                                                                <>
                                                                    <LabelWithHelp htmlFor="estimable" help={t('changes.help.estimable') || ''}>
                                                                        {t('changes.estimable')}
                                                                    </LabelWithHelp>
                                                                    <ToggleField
                                                                        name="estimable"
                                                                        onChange={v => { onSaveField('estimable', v); }}
                                                                        isLoading={ updating === 'pending' }
                                                                    />
                                                                </>
                                                            )}
                                                            <label htmlFor="estimate" className="h-4 flex justify-center items-center gap-1 text-xs text-gray-900 dark:text-gray-100 mt-1">
                                                                {isLoadingtracks && (
                                                                    <Icon type="loading" size={3} />
                                                                )}
                                                                {!isLoadingtracks && trackedDuration && (
                                                                    <>
                                                                        <Icon type="timer" size={3} />
                                                                        {trackedDuration}
                                                                        <FieldHelp content={t('changes.help.tracked_duration') || ''} />
                                                                    </>
                                                                )}
                                                            </label>
                                                        </div>
                                                    )}

                                                    {isEstimationStepOk && change.estimable && (
                                                        <div className="col-span-full md:col-span-2 flex flex-col items-center gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2">
                                                            <LabelWithHelp htmlFor="billable" help={t('changes.help.billable') || ''}>
                                                                {t('changes.billable')}
                                                            </LabelWithHelp>
                                                            {user?.can('change:estimate') ? (
                                                                <>
                                                                    {change.billable && (change.quote || change.isBilledHourly()) ? (
                                                                        <Icon type="check" size={8} />
                                                                    ) : (
                                                                        <ToggleField
                                                                            name="billable"
                                                                            onChange={v => { onSaveField('billable', v); }}
                                                                            isLoading={ updating === 'pending' }
                                                                        />
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Icon type={change.billable ? 'check' : 'close'} size={8} />
                                                                </>
                                                            )}
                                                            {change.billable && (
                                                                <span className="text-xs mt-2 inline-flex items-center gap-1">
                                                                    {t(`changes.billing_method_${change.billingMethod}`)}
                                                                    <FieldHelp content={t('changes.help.billing_method') || ''} />
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {canShowPlanFields && (
                                                        <>
                                                            <div className={`col-span-full md:col-span-${deadlineColSpan} flex flex-col items-center gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2`}>
                                                                <label htmlFor="deadline" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                    {t('changes.deadline')}
                                                                </label>
                                                                {user?.can('change:plan') ? (
                                                                    <DatepickerField name="deadline" onSave={ onSaveField } direction="left" inputClassName="bg-gray-50 dark:bg-gray-900 border-gray-50"/>
                                                                ) : (
                                                                    <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 py-2">
                                                                        {change.deadline ? formatMomentDate(change.deadline, 'LL') : '-'}
                                                                    </label>
                                                                )}
                                                                { change.deadline && (
                                                                    <ReminderWidget change={ change } />
                                                                )}
                                                            </div>
                                                            <div className="col-span-full">
                                                                <Sprints change={change} readOnly={!user?.can('change:plan')} />
                                                            </div>
                                                        </>
                                                    )}
                                                    {canShowAssignee && (
                                                    <div className="col-span-full flex flex-col items-center gap-x1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2">
                                                        <label htmlFor="assignee" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                            {t('changes.assignee')}
                                                        </label>
                                                        {user?.can('change:assign_self') && (
                                                            <div className="mt-2 border w-full bg-slate-50 dark:bg-gray-800 p-2 rounded text-center">
                                                                <UserAvatarField name="assignee._id" users={assignableMembers} onSave={ onSaveField }/>
                                                            </div>
                                                        )}
                                                        {!user?.can('change:assign_self') && change.assignee && (
                                                            <label className="text-sm font-bold">{change.assignee.fullname()}</label>
                                                        )}
                                                        {!user?.can('change:assign_self') && !change.assignee && (
                                                            <label className="text-sm italic">{t('changes.no_assignee_yet')}</label>
                                                        )}
                                                    </div>
                                                    )}
                                                    {!progressDetailHidden && (
                                                    <div className="col-span-full">
                                                        <ProgressSteps change={change} onRemove={handleDiscard}/>
                                                    </div>
                                                    )}
                                                    <div id="checklists-component" className="col-span-full">
                                                        <Checklists change={change._id} />
                                                    </div>
                                                        </div>
                                                    </div>
                                                    {layoutExpanded && (
                                                        <div className={progressDetailHidden
                                                            ? 'relative w-6 shrink-0 self-stretch'
                                                            : 'relative hidden lg:block w-6 self-stretch'
                                                        }>
                                                            <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gray-100 dark:bg-gray-800" />
                                                            <button
                                                                type="button"
                                                                className="absolute left-1/2 top-0 z-30 inline-flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 shadow-sm hover:bg-slate-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:text-gray-100"
                                                                onClick={toggleProgressDetailCollapsed}
                                                                aria-expanded={!progressDetailCollapsed}
                                                                aria-label={(progressDetailHidden
                                                                    ? t('changes.expand_progress_detail')
                                                                    : t('changes.collapse_progress_detail')) || ''}
                                                                title={(progressDetailHidden
                                                                    ? t('changes.expand_progress_detail')
                                                                    : t('changes.collapse_progress_detail')) || ''}
                                                            >
                                                                <Icon type={progressDetailHidden ? 'right' : 'left'} size={3} color="current" />
                                                            </button>
                                                        </div>
                                                    )}
                                                    <div
                                                        ref={layoutExpanded ? activityScrollRef : undefined}
                                                        className={layoutExpanded
                                                            ? 'min-h-0 min-w-0 pl-3 pb-4 lg:h-full lg:overflow-y-auto'
                                                            : 'contents'
                                                        }
                                                        style={layoutExpanded
                                                            ? { overflowAnchor: 'none' }
                                                            : undefined}
                                                    >
                                                    <div className={[
                                                        layoutExpanded ? '' : 'col-span-full',
                                                        layoutExpanded && pinComposerOutsideScroll && communicationComposerOpen
                                                            ? 'max-h-[55vh] overflow-y-auto'
                                                            : '',
                                                    ].filter(Boolean).join(' ') || undefined}>
                                                    {progressDetailHidden && (
                                                        <div className="mb-2 border-b border-gray-100 dark:border-gray-700 pb-2">
                                                            <ProgressSteps change={change} onRemove={handleDiscard} variant="actions" />
                                                        </div>
                                                    )}
                                                    {!isPendingTriage && (
                                                    <div className="col-span-full">
                                                            <div
                                                                ref={communicationToolbarRef}
                                                                id="change-modal-communication-toolbar"
                                                                className="mb-3 flex flex-wrap gap-2 justify-center"
                                                            >
                                                                {isClientUser ? (
                                                                    <button
                                                                        type="button"
                                                                        className={`px-3 py-1.5 text-sm rounded border ${communicationComposerOpen ? 'bg-white dark:bg-gray-800 shadow text-blue-700 font-medium border-blue-200 dark:border-blue-800' : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'}`}
                                                                        onClick={() => openCommunicationComposer('reply')}
                                                                    >
                                                                        <Icon type="reply" color="current" className="inline mr-1" />
                                                                        {t('changes.send_message') || t('common.reply')}
                                                                        {(htmlToPlainText(replyDraft) || replyQuoteHtml) ? (
                                                                            <span className="ml-1.5 rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-medium">
                                                                                {t('changes.draft_indicator') || ''}
                                                                            </span>
                                                                        ) : null}
                                                                    </button>
                                                                ) : (
                                                                    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-1">
                                                                        <button
                                                                            type="button"
                                                                            className={`px-3 py-1.5 text-sm rounded ${communicationComposerOpen && communicationTab === 'reply' ? 'bg-white dark:bg-gray-800 shadow text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-600 dark:text-gray-300'}`}
                                                                            onClick={() => openCommunicationComposer('reply')}
                                                                        >
                                                                            <Icon type="reply" color="current" className="inline mr-1" />
                                                                            {t('common.reply')}
                                                                            {(htmlToPlainText(replyDraft) || replyQuoteHtml) ? (
                                                                                <span className="ml-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 text-[10px] font-medium">
                                                                                    {t('changes.draft_indicator') || ''}
                                                                                </span>
                                                                            ) : null}
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className={`px-3 py-1.5 text-sm rounded ${communicationComposerOpen && communicationTab === 'comment' ? 'bg-white dark:bg-gray-800 shadow text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-600 dark:text-gray-300'}`}
                                                                            onClick={() => openCommunicationComposer('comment')}
                                                                        >
                                                                            <Icon type="comment" color="current" className="inline mr-1" />
                                                                            {t('changes.communication_comment') || ''}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {communicationComposerOpen && communicationTab === 'reply' && (
                                                                <div className="relative z-[90] rounded border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                                                <div className="mb-3">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <label className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                            <Icon type="email" className="inline mr-1.5" />
                                                                            {isClientUser ? (t('changes.send_message') || '') : (t('common.reply') || '')}
                                                                        </label>
                                                                        <div className="flex items-center gap-3">
                                                                        {!isClientUser && (
                                                                            <button
                                                                                type="button"
                                                                                className="flex items-center text-xs cursor-pointer font-normal border-none text-gray-500 dark:text-gray-400 hover:underline bg-transparent p-0"
                                                                                onClick={() => toggleReplyRecipientsEditor('composer')}
                                                                            >
                                                                                <Icon
                                                                                    type={replyRecipientsEditorVisible && replyRecipientsEditorAnchor === 'composer' ? 'close' : 'edit'}
                                                                                    className="inline mr-0.5"
                                                                                    size={3}
                                                                                />
                                                                                <span>
                                                                                    {replyRecipientsEditorVisible && replyRecipientsEditorAnchor === 'composer'
                                                                                        ? t('common.cancel')
                                                                                        : t('changes.reply_recipients_edit')}
                                                                                </span>
                                                                            </button>
                                                                        )}
                                                                            {composerPinToggle}
                                                                            <button
                                                                                type="button"
                                                                                className="flex items-center text-xs cursor-pointer font-normal border-none text-gray-500 dark:text-gray-400 hover:underline bg-transparent p-0"
                                                                                onClick={() => setCommunicationComposerOpen(false)}
                                                                            >
                                                                                <Icon type="close" className="inline mr-0.5" size={3} />
                                                                                <span>{t('changes.close_composer') || t('common.cancel')}</span>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="mt-2 text-xs text-gray-700 dark:text-gray-200 space-y-1 border-t border-gray-200 dark:border-gray-700 pt-2">
                                                                        <div className="text-gray-800 dark:text-gray-100">
                                                                            <span className="font-semibold">{t('changes.reply_preview_title')}:</span>{' '}
                                                                            {isLoadingReplyRecipientsPreview ? (
                                                                                <span className="italic text-gray-500 dark:text-gray-400">{t('changes.reply_preview_loading')}</span>
                                                                            ) : notifiedRecipientItems.length === 0 ? (
                                                                                <span>{t('changes.reply_preview_none')}</span>
                                                                            ) : (
                                                                                <span className="inline-flex flex-wrap gap-1.5 align-middle mt-1">
                                                                                    {notifiedRecipientItems.map((item) => (
                                                                                        <span
                                                                                            key={item.key}
                                                                                            className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-0.5 text-xs text-gray-800 dark:text-gray-100"
                                                                                            title={item.channel === 'signal'
                                                                                                ? (t('changes.reply_preview_signal_users') || 'Signal')
                                                                                                : (t('changes.reply_preview_email_external') || 'Email')}
                                                                                        >
                                                                                            <Icon
                                                                                                type={item.channel === 'signal' ? 'signal' : 'email'}
                                                                                                size={3}
                                                                                                className="inline text-gray-500 dark:text-gray-400"
                                                                                            />
                                                                                            <span>{item.label}</span>
                                                                                            {item.reasons.map((reason) => (
                                                                                                <span
                                                                                                    key={`${item.key}-${reason}`}
                                                                                                    className="rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                                                                                                    title={t(replyPreviewReasonI18nKey(reason)) || reason}
                                                                                                >
                                                                                                    {t(replyPreviewReasonI18nKey(reason)) || reason}
                                                                                                </span>
                                                                                            ))}
                                                                                        </span>
                                                                                    ))}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {showQuotedAuthorNotRecipientWarning && quotedReplyAuthor && (
                                                                        <div className="mt-2 rounded border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 px-2 py-2 text-xs text-amber-900 dark:text-amber-100">
                                                                            <p>{t('changes.reply_quoted_author_not_recipient', { name: quotedReplyAuthor.displayName })}</p>
                                                                            <div className="mt-2">
                                                                                <Button
                                                                                    title={t('changes.reply_quoted_author_add') || ''}
                                                                                    color="warning"
                                                                                    small
                                                                                    onClick={addQuotedAuthorToRecipients}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {replyRecipientsEditorAnchor === 'composer' && replyRecipientsEditorNode}

                                                                    {replyRecipientsEditorAnchor === 'composer' && replyRecipientsDirty && (
                                                                        <div className="mt-2 text-xs text-amber-700">
                                                                            {t('changes.reply_recipients_unsaved')}
                                                                        </div>
                                                                    )}
                                                                    {replyRecipientsEditorAnchor === 'composer' && replyRecipientsError && (
                                                                        <div className="mt-2">
                                                                            <Alert title={replyRecipientsError} icon="warning" color="failure" />
                                                                        </div>
                                                                    )}
                                                                    {replyRecipientsEditorAnchor === 'composer' && replyRecipientsSuccess && (
                                                                        <div className="mt-2">
                                                                            <Alert title={replyRecipientsSuccess} icon="check" color="success" />
                                                                        </div>
                                                                    )}
                                                                    {replyRecipientsPreviewError && (
                                                                        <div className="mt-2">
                                                                            <Alert title={replyRecipientsPreviewError} icon="warning" color="warning" />
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                                                    <label className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                        {t('changes.reply_response')}
                                                                    </label>
                                                                    <div className="flex items-center gap-2 flex-wrap justify-end">
                                                                        {!isClientUser && (
                                                                            <>
                                                                            <select
                                                                                className="text-sm rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-1 pl-2 pr-6 text-gray-700 dark:text-gray-200 max-w-[220px]"
                                                                                value=""
                                                                                disabled={replyTemplatesLoading || replyTemplates.length === 0}
                                                                                onChange={(e) => {
                                                                                    const id = e.target.value;
                                                                                    const template = replyTemplates.find(item => item._id === id);
                                                                                    if (template)
                                                                                        insertReplyTemplate(template);
                                                                                    e.target.value = '';
                                                                                }}
                                                                                title={t('reply_templates.insert') || ''}
                                                                            >
                                                                                <option value="">
                                                                                    {replyTemplatesLoading
                                                                                        ? (t('common.loading') || '')
                                                                                        : (replyTemplates.length
                                                                                            ? (t('reply_templates.insert') || '')
                                                                                            : (t('reply_templates.empty_short') || ''))}
                                                                                </option>
                                                                                {replyTemplates.map((template: ReplyTemplate) => (
                                                                                    <option key={template._id} value={template._id}>
                                                                                        {template.isGlobal()
                                                                                            ? `${template.name} (${t('reply_templates.scope_global')})`
                                                                                            : template.name}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                            <button
                                                                                type="button"
                                                                                className="text-xs text-blue-700 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                                                                                onClick={() => setReplyTemplatesManageOpen(true)}
                                                                            >
                                                                                {t('reply_templates.manage')}
                                                                            </button>
                                                                            {/* preventDefault keeps Draft.js selection when clicking Improve */}
                                                                            <span
                                                                                className="inline-flex"
                                                                                onMouseDown={(e) => e.preventDefault()}
                                                                            >
                                                                                <Button
                                                                                    title={isSuggestingReply
                                                                                        ? ((htmlToPlainText(replyDraft)
                                                                                            ? t('changes.reply_improving')
                                                                                            : t('changes.reply_suggesting')) || '')
                                                                                        : ((htmlToPlainText(replyDraft)
                                                                                            ? t('changes.reply_improve')
                                                                                            : t('changes.reply_suggest')) || '')}
                                                                                    icon="magic"
                                                                                    color="secondary"
                                                                                    small
                                                                                    loading={isSuggestingReply}
                                                                                    onClick={() => suggestReply()}
                                                                                />
                                                                            </span>
                                                                            </>
                                                                        )}
                                                                        <Button
                                                                            title={isSendingReply ? (t('changes.reply_sending') || '') : (t('changes.reply_send') || '')}
                                                                            icon="email"
                                                                            color="primary"
                                                                            small
                                                                            loading={isSendingReply}
                                                                            onClick={() => sendReply()}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div id="change-modal-reply-wysiwyg-anchor">
                                                                <Wysiwyg
                                                                    ref={replyWysiwygRef}
                                                                    value={replyDraft}
                                                                    onChange={(value: string, editorState: EditorState) => {
                                                                        setReplyDraft(value);
                                                                        replyEditorStateRef.current = editorState;
                                                                        setReplyEditorState(editorState);
                                                                    }}
                                                                    onEditorStateChange={(editorState: EditorState) => {
                                                                        replyEditorStateRef.current = editorState;
                                                                    }}
                                                                    className="text-gray-900 dark:text-gray-100 placeholder:text-gray-400 sm:text-sm sm:leading-6 Wysiwyg--reply"
                                                                    rows={6}
                                                                    minimal
                                                                />
                                                                </div>
                                                                {replyQuoteHtml ? (
                                                                    <div className="mt-2 rounded border border-gray-200 bg-white/70 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/80">
                                                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                                                            <button
                                                                                type="button"
                                                                                className="yoda-quoted-toggle__button text-xs"
                                                                                aria-expanded={replyQuoteExpanded}
                                                                                onClick={() => setReplyQuoteExpanded((value) => !value)}
                                                                            >
                                                                                {replyQuoteExpanded
                                                                                    ? (t('changes.hide_quoted_text') || '')
                                                                                    : (t('changes.show_quoted_text') || '')}
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="text-xs text-gray-500 dark:text-gray-400 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                                                                                onClick={() => {
                                                                                    setReplyQuoteHtml('');
                                                                                    setReplyQuoteExpanded(false);
                                                                                    setQuotedReplyAuthor(null);
                                                                                }}
                                                                            >
                                                                                {t('changes.remove_quoted_text') || ''}
                                                                            </button>
                                                                        </div>
                                                                        {replyQuoteExpanded && (
                                                                            <div className="yoda-quoted-history mt-2 text-sm text-gray-700 dark:text-gray-200">
                                                                                <HtmlContent
                                                                                    html={replyQuoteHtml}
                                                                                    projectId={change?.project?._id}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : null}
                                                                <div>
                                                                    <Uploader
                                                                        projectId={change?.project?._id}
                                                                        changeId={change?._id}
                                                                        onUpload={insertReplyAttachment}
                                                                        hideFiles
                                                                    />
                                                                </div>
                                                                <div className="mt-2 flex justify-end gap-2">
                                                                    <Button
                                                                        title={t('common.cancel') || ''}
                                                                        icon="close"
                                                                        color="none"
                                                                        textColor="red-500"
                                                                        iconColor="red-500"
                                                                        onClick={cancelCommunicationComposer}
                                                                    />
                                                                    <Button
                                                                        title={isSendingReply ? (t('changes.reply_sending') || '') : (t('changes.reply_send') || '')}
                                                                        icon="email"
                                                                        color="primary"
                                                                        loading={isSendingReply}
                                                                        onClick={() => sendReply()}
                                                                    />
                                                                </div>
                                                                {replyError && (
                                                                    <div className="mt-3">
                                                                        <Alert title={replyError} icon="warning" color="failure" />
                                                                    </div>
                                                                )}
                                                                {replySuccess && (
                                                                    <div className="mt-3">
                                                                        <Alert title={replySuccess} icon="check" color="success" />
                                                                    </div>
                                                                )}
                                                                </div>
                                                            )}

                                                            {communicationComposerOpen && communicationTab === 'comment' && !isClientUser && (
                                                                <div className="relative z-[90] rounded border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                                                    <div className="mb-2 flex justify-end gap-3">
                                                                        {composerPinToggle}
                                                                        <button
                                                                            type="button"
                                                                            className="flex items-center text-xs cursor-pointer font-normal border-none text-gray-500 dark:text-gray-400 hover:underline bg-transparent p-0"
                                                                            onClick={() => setCommunicationComposerOpen(false)}
                                                                        >
                                                                            <Icon type="close" className="inline mr-0.5" size={3} />
                                                                            <span>{t('changes.close_composer') || t('common.cancel')}</span>
                                                                        </button>
                                                                    </div>
                                                                    <div id="change-modal-comment-composer-anchor">
                                                                        <ChangeActivity
                                                                            members={ members }
                                                                            change={ change }
                                                                            showComposer
                                                                            hideActivityToggle
                                                                            composerTitle={t('changes.add_comment') || ''}
                                                                            onReplyFromTimeline={isPendingTriage ? undefined : handleQuoteFromTimeline}
                                                                            onCommentCreated={() => {
                                                                                setCommentDraft('');
                                                                                setCommunicationComposerOpen(false);
                                                                            }}
                                                                            onComposerCancel={cancelCommunicationComposer}
                                                                            commentDraft={commentDraft}
                                                                            onCommentDraftChange={setCommentDraft}
                                                                            onCreateLinkedChange={
                                                                                (change.isMerged?.() || change.progress?.status === Change.STATUS_DISCARDED)
                                                                                    ? undefined
                                                                                    : (comment) => createLinkedChange(comment)
                                                                            }
                                                                            creatingLinkedChange={isCreatingLinked}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    </div>
                                                    <div className="col-span-full">
                                                        <ShowRemoteImagesButton className="mb-2" />
                                                        <ChangeActivity
                                                            members={ members }
                                                            change={ change }
                                                            showComposer={false}
                                                            hideActivityToggle
                                                            alwaysShowActivity
                                                            hideJumpRail={communicationComposerOpen}
                                                            onReplyFromTimeline={isPendingTriage ? undefined : handleQuoteFromTimeline}
                                                            onCreateLinkedChange={
                                                                (change.isMerged?.() || change.progress?.status === Change.STATUS_DISCARDED)
                                                                    ? undefined
                                                                    : (comment) => createLinkedChange(comment)
                                                            }
                                                            creatingLinkedChange={isCreatingLinked}
                                                        />
                                                    </div>
                                                    </div>
                                                </div>
                                            </Tabs.Item>
                                            {change.billable && (
                                                <Tabs.Item icon={() => <Icon type="law" color="current" className="mr-1.5 outline-none" />} title={t('changes.step_budget')}>
                                                    <div className={layoutExpanded ? 'h-full min-h-0 overflow-y-auto' : undefined}>
                                                        {change.estimate > 0 ? (
                                                            <BudgetSteps change={change} />
                                                            ) : (
                                                            <div className="col-span-full">
                                                                <Alert title={t('changes.estimate_to_continue')} icon="warning" color="warning"/>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Tabs.Item>
                                            )}
                                            <Tabs.Item
                                                icon={() => <Icon type="file" color="current" className="mr-1.5 outline-none" />}
                                                title={t('changes.step_file') + (change?.nbAttachments > 0 ? ' (' + change.nbAttachments +')' : '')}
                                            >
                                                <div className={layoutExpanded ? 'h-full min-h-0 overflow-y-auto' : undefined}>
                                                    <Uploader projectId={project?._id} changeId={change._id} />
                                                </div>
                                            </Tabs.Item>
                                            {!isClientUser && (
                                                <Tabs.Item
                                                    icon={() => <Icon type="email" color="current" className="mr-1.5 outline-none" />}
                                                    title={t('changes.step_notifications')}
                                                >
                                                    <div className={layoutExpanded ? 'h-full min-h-0 overflow-y-auto pb-6' : 'pb-2'}>
                                                    <div className="grid grid-cols-1 gap-4">
                                                        <div>
                                                            <div className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                                                                {t('changes.completion_notify')}
                                                                <FieldHelp content={t('changes.help.completion_notify') || ''} />
                                                            </div>
                                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                                {t('changes.completion_notify_help')}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                                                {t('changes.completion_notify_emails')}
                                                            </label>
                                                            <div className="mt-1">
                                                                <EmailSuggestField
                                                                    value={completionEmailInput}
                                                                    onChange={setCompletionEmailInput}
                                                                    onCommit={(email) => addCompletionEmail(email)}
                                                                    sources={recipientEmailSources}
                                                                    excludedEmails={completionNotify?.emails || []}
                                                                    placeholder={t('changes.reply_add_email_placeholder') || ''}
                                                                />
                                                            </div>
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {(completionNotify?.emails || []).map((email: string) => (
                                                                    <span key={`completion-email-${email}`} className="inline-flex items-center rounded bg-blue-100 text-blue-900 dark:bg-blue-900/50 dark:text-blue-100 px-2 py-1 text-xs">
                                                                        {email}
                                                                        <button className="ml-1 inline-flex text-current" type="button" onClick={() => removeCompletionEmail(email)} aria-label={t('common.delete') || ''}>
                                                                            <Icon type="close" size={3} color="inherit" />
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                            {completionNotifyError && (
                                                                <div className="mt-2">
                                                                    <Alert title={completionNotifyError} icon="warning" color="failure" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                                                    {t('changes.completion_notify_users')}
                                                                </label>
                                                                <div className="mt-1">
                                                                    <MultiSelect
                                                                        options={(users || [])
                                                                            .filter((member: User) => !!member.phone)
                                                                            .sort((a: User, b: User) => a.fullname().localeCompare(b.fullname()))
                                                                            .map((member: User) => ({
                                                                                value: member._id,
                                                                                label: member.fullname()
                                                                            }))}
                                                                        defaultValue={completionNotify?.signalUsers || []}
                                                                        placeholder={t('changes.completion_notify_users') || ''}
                                                                        onChange={(value) => updateCompletionNotify('signalUsers', value as string[])}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                                                    {t('changes.completion_notify_groups')}
                                                                </label>
                                                                <div className="mt-1">
                                                                    <MultiSelect
                                                                        options={(groups || [])
                                                                            .filter((group: AnyObj) => !!group.name)
                                                                            .sort((a: AnyObj, b: AnyObj) => String(a.name).localeCompare(String(b.name)))
                                                                            .map((group: AnyObj) => ({
                                                                                value: group._id,
                                                                                label: group.name
                                                                            }))}
                                                                        defaultValue={completionNotify?.signalGroups || []}
                                                                        placeholder={t('changes.completion_notify_groups') || ''}
                                                                        onChange={(value) => updateCompletionNotify('signalGroups', value as string[])}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    </div>
                                                </Tabs.Item>
                                            )}
                                            {!isClientUser && change.isDev() && mr && (
                                                <Tabs.Item
                                                    icon={() => <Icon type="gitlab" color="current" className="mr-1.5 outline-none" />}
                                                    title={t('changes.step_merge_request')}
                                                >
                                                    <div className={layoutExpanded ? 'h-full min-h-0 overflow-y-auto' : undefined}>
                                                        <ChangeMergeRequestPanel
                                                            changeId={change._id}
                                                            mrLink={mr.link}
                                                        />
                                                    </div>
                                                </Tabs.Item>
                                            )}
                                            {change.isDev() && (
                                                <Tabs.Item icon={() => <Icon type="release" color="current" className="mr-1.5 outline-none" />} title={t('changes.step_release')}>
                                                    <div className={layoutExpanded ? 'h-full min-h-0 overflow-y-auto' : undefined}>
                                                    <div className="m-0 mb-5 p-0 w-full flex flex-col items-start gap-1 border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2">
                                                        <LabelWithHelp htmlFor="omitFromChangelog" help={t('changes.help.omit_from_changelog') || ''}>
                                                            {t('changes.omit_from_changelog')}
                                                        </LabelWithHelp>
                                                        {user?.can('change:edit') ? (
                                                            <ToggleField
                                                                name="omitFromChangelog"
                                                                onChange={v => { onSaveField('omitFromChangelog', v); }}
                                                                isLoading={ updating === 'pending' }
                                                            />
                                                        ) : (
                                                            <Icon type={change.omitFromChangelog ? 'check' : 'close'} size={8} />
                                                        )}
                                                    </div>
                                                    {change.estimate > 0 && (
                                                        <>
                                                            <div className="m-0 mb-5 p-0 w-full">
                                                                <TranslatableField
                                                                    as="input"
                                                                    type="text"
                                                                    name="release_notes.title"
                                                                    onChange={ onChangeField }
                                                                    onSave={ onSaveField }
                                                                    className="font-bold"
                                                                    langs={project?.translation?.languages?.all}
                                                                    label={(
                                                                        <LabelWithHelp htmlFor="release_notes.title" help={t('changes.help.release_notes_title') || ''}>
                                                                            {t('changes.release_notes.title')}
                                                                        </LabelWithHelp>
                                                                    )}
                                                                    required
                                                                />
                                                                <ErrorMessage name="title" component="div" />
                                                            </div>
                                                            <div className="m-0 mb-5 p-0 w-full">
                                                                <div className="mt-2">
                                                                    <TranslatableWysiwygField
                                                                        name="release_notes.description"
                                                                        inline
                                                                        langs={project?.translation?.languages?.all}
                                                                        placeholder={t('changes.enter_description') || ''}
                                                                        rows={4}
                                                                        label={(
                                                                            <LabelWithHelp htmlFor="release_notes.description" help={t('changes.help.release_notes_description') || ''}>
                                                                                {t('changes.release_notes.description')}
                                                                            </LabelWithHelp>
                                                                        )}
                                                                        onChange={ onChangeField }
                                                                        onSave={ onSaveField }
                                                                        isLoading={ updating === 'pending' }
                                                                    />
                                                                </div>
                                                            </div>
                                                            <ImageUploader hidePreview onChange={onImageRelease}>
                                                                <div className="col-span-full">
                                                                    {change.release_notes?.image && (
                                                                        <>
                                                                            <img src={change.getReleaseImageUrl(80, true) || ''} className="w-full max-h-80 object-contain" />
                                                                            <Button
                                                                                title=""
                                                                                icon="delete"
                                                                                onClick={removeImageRelease}
                                                                                iconColor="red-600"
                                                                                className="group-hover:block absolute -bottom-3 -right-5"
                                                                                color="none"
                                                                            />
                                                                        </>
                                                                    )}
                                                                    {!change.release_notes.image && (
                                                                        <div className="group relative">
                                                                            <div className="flex items-center justify-center">
                                                                                <Icon type="file" color="current" className="inline mr-1 text-gray-700 dark:text-gray-200"/>
                                                                                <div className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                                    {t('attachments.drag_files')}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </ImageUploader>
                                                        </>
                                                    )}
                                                    {!(change.estimate > 0) && (
                                                        <div className="col-span-full">
                                                            <Alert title={t('changes.estimate_to_continue')} icon="warning" color="warning"/>
                                                        </div>
                                                    )}
                                                    </div>
                                                </Tabs.Item>
                                            )}
                                        </Tabs>
                                        </div>
                                    </div>
                                </Transition>
                                {moveModalIsVisible && (
                                    <div className="absolute top-0 left-0 right-0 bottom-0 flex justify-center items-center bg-gray-900 bg-opacity-50 rounded-lg">
                                        <div className="w-[300px] rounded border flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                                            <MoveChangeModal
                                                show={moveModalIsVisible}
                                                onClose={() => showMoveModal(false)}
                                                initialBoardId={board?._id}
                                            />
                                        </div>
                                    </div>
                                )}
                            </ChangeModalBody>
                        </Form>
                        </RemoteImagesProvider>
                    </Formik>
                </ChangeModalFrame>
                <MergeChangesModal
                    show={mergeModalIsVisible}
                    sources={change ? [change] : []}
                    processing={isMerging}
                    error={mergeError}
                    defaultProjectId={change?.project?._id}
                    title={t('changes.merge_this_change') as string}
                    confirmLabel={t('priorities.merge_apply') as string}
                    onClose={closeMergeModal}
                    onApply={applyMerge}
                />
                {change && (
                    <RelatedChangesModal
                        show={relatedModalIsVisible}
                        current={change}
                        excludeIds={(change.related || []).map((item: Change) => item._id)}
                        processing={isLinkingRelated}
                        onClose={() => setRelatedModalVisible(false)}
                        onApply={applyRelatedLink}
                    />
                )}
                <ReplyTemplatesManageModal
                    show={replyTemplatesManageOpen}
                    projectId={resolveProjectId()}
                    projectName={
                        (typeof change?.project === 'object' && change?.project?.name)
                        || project?.name
                        || null
                    }
                    onClose={() => {
                        setReplyTemplatesManageOpen(false);
                        loadReplyTemplates();
                    }}
                />

                <Confirm
                    visible={!!pendingNonDevTypeFields}
                    title={t('changes.are_you_sure_to_switch_non_dev') || ''}
                    confirmText={t('changes.confirm_switch_non_dev') || ''}
                    onConfirm={() => pendingNonDevTypeFields && saveTypeFields(pendingNonDevTypeFields)}
                    onCancel={() => setPendingNonDevTypeFields(null)}
                />

                <Confirm
                    visible={showAttachmentWordingConfirm}
                    title={t('changes.reply_attachment_wording_confirm') || ''}
                    confirmText={t('changes.reply_send') || ''}
                    onConfirm={() => sendReply({ skipAttachmentWordingCheck: true })}
                    onCancel={() => setShowAttachmentWordingConfirm(false)}
                />
            </div>
        );
    }, [
        show,
        change?.updatedAt,
        change?.progress?.status,
        change?.logs?.length,
        change?.labelIds,
        project?.labels,
        project?.updatedAt,
        change?.quote,
        change?.billable,
        change?.isBilledHourly,
        change?.comments?.length,
        pendingNonDevTypeFields,
        updating,
        updateStatus,
        handleDiscard,
        handleRestore,
        deleteError,
        isLoadingtracks,
        moveModalIsVisible,
        mergeModalIsVisible,
        isMerging,
        mergeError,
        relatedModalIsVisible,
        isLinkingRelated,
        change?.related,
        change?.subscribers,
        replyDraft,
        commentDraft,
        replyQuoteHtml,
        replyQuoteExpanded,
        replyError,
        replySuccess,
        isSuggestingReply,
        isSendingReply,
        showAttachmentWordingConfirm,
        communicationTab,
        communicationComposerOpen,
        composerPinned,
        toggleComposerPinned,
        isExpanded,
        headerDetailsExpanded,
        toggleHeaderDetails,
        i18n.language,
        progressDetailCollapsed,
        editingMeta,
        selectedClient,
        replyRecipientsEditorVisible,
        replyRecipientsEditorAnchor,
        replyRecipients,
        replyRecipientsDirty,
        toEmailInput,
        ccEmailInput,
        isSavingReplyRecipients,
        replyRecipientsError,
        replyRecipientsSuccess,
        isTogglingChangeNotifications,
        isSavingSubscribers,
        replyRecipientsPreview,
        notifiedRecipientItems,
        replyRecipientsPreviewError,
        isLoadingReplyRecipientsPreview,
        completionNotify,
        completionEmailInput,
        completionNotifyError,
        members,
        subscriberCandidates,
        users,
        groups,
        isClientUser,
        handleQuoteFromTimeline,
        handleModalClose,
        toggleExpanded,
        relatedExpanded,
        toggleRelatedExpanded,
        toggleProgressDetailCollapsed,
        remoteImageHtmlSources,
        isPinned,
        onTogglePin,
        sideOffset,
        sideOffsetClass,
        standalone,
        ensureClientsLoaded,
        canShowPlanFields,
        canShowAssignee,
        isEstimationStepOk,
        estimateColSpan,
        deadlineColSpan,
    ]);

    const changeMatchesRequested = !changeId || change?._id === changeId;

    if (show && change && changeMatchesRequested && change.slug != null) {
        setOpenChangeDocumentTitle(
            formatChangeDocumentTitle(
                change.slugName(),
                change.localizedTitle(i18n.language) || ''
            )
        );
    }

    if (show && changeNotFound && !changeMatchesRequested) {
        return (
            <Modal show={true} onClose={handleModalClose} dismissible size="md">
                <Modal.Header>{t('changes.not_found')}</Modal.Header>
                <Modal.Body>
                    <div className="flex flex-col items-center justify-center gap-4 py-6 text-gray-500 dark:text-gray-400">
                        <Icon type="sad" size={10} />
                        <p className="text-lg">{t('changes.not_found')}</p>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    if (show && !change) {
        if (standalone)
            return <ChangeStandaloneSkeleton />;
        return <Loader />;
    }

    if (!change)
        return null;

    return content;
}

export default ChangeModal;

